import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Chat as ChatIcon, Close, QrCodeScanner, RestartAlt, Send } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { uploadAssetPhoto } from '../services/assetService'
import type {
  AssistantContext,
  AssistantOption,
  AssistantState
} from '../services/assistantService'
import {
  chatWithAssistant,
  readDeviceCodeWithAI,
  readHeaterLabelWithAI
} from '../services/assistantService'
import { getProjects } from '../services/projectService'
import {
  getDeviceByName,
  getPairedRuuviTags,
  getProjectDevices,
  invokeAction
} from '../services/deviceService'
import { createOrUpdateRuuviTag } from '../services/ruuviTagService'
import { extractCompleteShortCode } from '../utils/deviceCode'
import { reduceImageFileSize } from '../utils/imageCrop'
import { formatMacAddress } from '../utils/string'
import { useLanguage } from '../contexts/LanguageContext'
import Snapshot3D from './installation/3DModel'
import type { PairedRuuviTag } from '../types'
import { computeRotationDegFromTag } from '../utils/rotation'
import {
  ChatWidgetDeviceImageUploadDialog,
  ChatWidgetOcrDialog,
  ChatWidgetQrScanDialog,
  type ScanDialogMode
} from './chatWidget/ChatWidgetDialogs'
import { useChatViewportScroll } from '../hooks/useChatViewportScroll'
import {
  appendNavigationPrompts,
  buildDeviceNotFoundReply,
  buildFloatingHelpReply,
  buildGuidedPrompts,
  buildSpineNameQuickPromptsFromReply,
  detectFloatingInstallShortcut,
  detectInstallModeChoice,
  extractReplyFromJsonLike,
  extractRuuviTagIdFromQr,
  findProjectByTypedName,
  getDefaultQuickReplyPrompts,
  getInstallModePrompts,
  getLocalizedAssistantTypingLabel,
  getLocalizedChatPanelSubtitle,
  getLocalizedChatPanelTitle,
  getLocalizedGoBackPrompt,
  getLocalizedQuickPromptSectionTitle,
  getLocalizedRetryPrompt,
  getLocalizedStartOverPrompt,
  getLocalizedYesNoPrompts,
  getWelcomeMessage,
  isCannotFindDeviceMessage,
  isInstallationReference,
  isInstallModeQuestionReply,
  isTooGeneralRequestReply,
  normalizeAssistantReply,
  parseYesNoAnswer,
  rankProjectOptionsByDistance
} from '../utils/chatWidgetUtils'
import {
  mapProjectDevicesToChatContext,
  type ChatWidgetDeviceContextItem
} from '../utils/chatWidgetDeviceContext'
import { inferAssistantInputType } from '../utils/chatWidgetInputType'
import type { AssistantInputType, UserCoordinates } from '../utils/chatWidgetUtils'

type ChatRole = 'user' | 'assistant'
type ChatMessageKind =
  | 'text'
  | 'valve_customer_reference_example'
  | 'valve_heater_nameplate_example'
type ChatDecisionType = 'customer_reference' | 'heater_details'

interface ChatMessage {
  id: string
  role: ChatRole
  content: string
  createdAt: number
  kind?: ChatMessageKind
  decisionType?: ChatDecisionType
}

type AssistantSelectableInputType = Extract<
  AssistantInputType,
  'project_select' | 'device_select' | 'tag_select'
>

type TagSelectionMode = 'qr' | 'bluetooth'

type PendingInterpretedDeviceCode = {
  code: string
  matchedDeviceName?: string
}

type PendingInterpretedHeaterDetails = {
  detailsMessage: string
  displaySummary: string
}

type PendingInterpretedCustomerReference = {
  value: string
}

type AssistantUiSnapshot = {
  state: AssistantState
  inputType: AssistantInputType
  options: AssistantOption[]
  guidedPrompts: string[]
}

const DEFAULT_ASSISTANT_STATE: AssistantState = {
  workflow: 'none',
  step: 'idle'
}

const ASSET_PHOTO_UPLOAD_MAX_BYTES = 5 * 1024 * 1024
const ASSET_PHOTO_UPLOAD_TARGET_BYTES = 4.8 * 1024 * 1024

function createAssistantUiSnapshot(
  state: AssistantState,
  inputType: AssistantInputType,
  options: AssistantOption[],
  guidedPrompts: string[]
): AssistantUiSnapshot {
  return {
    state: { ...state },
    inputType,
    options: options.map((option) => ({ ...option })),
    guidedPrompts: Array.from(
      new Set(guidedPrompts.map((prompt) => prompt.trim()).filter((prompt) => prompt.length > 0))
    )
  }
}

function areAssistantUiSnapshotsEqual(a: AssistantUiSnapshot, b: AssistantUiSnapshot): boolean {
  if (a.inputType !== b.inputType) {
    return false
  }

  if (a.guidedPrompts.length !== b.guidedPrompts.length) {
    return false
  }

  if (a.options.length !== b.options.length) {
    return false
  }

  if (JSON.stringify(a.state) !== JSON.stringify(b.state)) {
    return false
  }

  for (let index = 0; index < a.guidedPrompts.length; index += 1) {
    if (a.guidedPrompts[index] !== b.guidedPrompts[index]) {
      return false
    }
  }

  for (let index = 0; index < a.options.length; index += 1) {
    const left = a.options[index]
    const right = b.options[index]

    if (left.value !== right.value || left.label !== right.label) {
      return false
    }
  }

  return true
}

function normalizeCustomerReferenceValue(value: string | null | undefined): string | null {
  if (!value) {
    return null
  }

  const collapsed = value.replace(/\s+/g, '').trim()
  const cleaned = collapsed.replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9]+$/g, '')

  return cleaned.length > 0 ? cleaned : null
}

function parseRuuviTagIdentifierForComparison(value: string): string | null {
  const trimmed = value.trim()

  if (!trimmed) {
    return null
  }

  const macWithSeparators = value.match(/([0-9a-fA-F]{2}[:-]){5}[0-9a-fA-F]{2}/)?.[0]

  if (macWithSeparators) {
    const normalized = macWithSeparators.replace(/[^0-9a-fA-F]/g, '').toUpperCase()

    return normalized.length === 12 ? normalized : null
  }

  const plainMac = value.match(/\b[0-9a-fA-F]{12}\b/)?.[0]

  if (plainMac) {
    return plainMac.toUpperCase()
  }

  const compactIdentifier = trimmed.replace(/\s+/g, '')

  if (/^[0-9A-Za-z_-]{6,64}$/.test(compactIdentifier)) {
    return compactIdentifier
  }

  return null
}

function normalizeRuuviTagIdentifierForComparison(value: string | null | undefined): string | null {
  if (!value) {
    return null
  }

  const parsedIdentifier = parseRuuviTagIdentifierForComparison(value)

  if (parsedIdentifier) {
    const normalizedParsedIdentifier = parsedIdentifier.trim().toLowerCase()

    return normalizedParsedIdentifier || null
  }

  const normalizedValue = value.trim().replace(/\s+/g, ' ').toLowerCase()

  return normalizedValue || null
}

function filterDiscoveredTagsExcluding(
  tags: PairedRuuviTag[],
  excludedTagId?: string
): PairedRuuviTag[] {
  const normalizedExcludedTagId = normalizeRuuviTagIdentifierForComparison(excludedTagId)

  if (!normalizedExcludedTagId) {
    return tags
  }

  return tags.filter((tag) => {
    const normalizedTagId = normalizeRuuviTagIdentifierForComparison(tag.id)

    return normalizedTagId !== normalizedExcludedTagId
  })
}

function extractBestCustomerReferenceCandidate(textLines: string[]): string | null {
  const normalizedLines = textLines.map((line) => line.trim()).filter((line) => line.length > 0)

  if (normalizedLines.length === 0) {
    return null
  }

  const keywordRegex = /asiakas|customer|reference|ref\.?|tunnus/i
  const serialContextRegex = /\b(s\/?n|serial|sarjanumero|serialnumber)\b/i
  const nonReferenceWordRegex =
    /\b(paluu|return|retur|meno|kaukora|oy|finland|raisio|jaspi|apuwatti|ce)\b/i

  const numericCandidates: Array<{ value: string; score: number }> = []

  normalizedLines.forEach((line, index) => {
    const previousLine = index > 0 ? normalizedLines[index - 1] : ''
    const nextLine = index < normalizedLines.length - 1 ? normalizedLines[index + 1] : ''
    const hasKeyword =
      keywordRegex.test(line) || keywordRegex.test(previousLine) || keywordRegex.test(nextLine)
    const isSerialContext = serialContextRegex.test(line) || serialContextRegex.test(previousLine)
    const hasNonReferenceWords = nonReferenceWordRegex.test(line)
    const numberMatches = line.match(/\d{4,10}/g) ?? []

    numberMatches.forEach((match) => {
      let score = 0

      if (match.length === 6) {
        score += 140
      } else if (match.length === 5 || match.length === 7) {
        score += 90
      } else {
        score += 60
      }

      if (hasKeyword) {
        score += 50
      }

      if (/^\D*\d{4,10}\D*$/.test(line)) {
        score += 20
      }

      if (/^(19|20)\d{2}$/.test(match)) {
        score -= 120
      }

      if (isSerialContext) {
        score -= 120
      }

      if (hasNonReferenceWords && !hasKeyword) {
        score -= 35
      }

      numericCandidates.push({ value: match, score })
    })
  })

  const bestNumericCandidate = numericCandidates
    .filter((candidate) => candidate.score >= 40)
    .sort((a, b) => b.score - a.score)[0]

  if (bestNumericCandidate?.value) {
    return bestNumericCandidate.value
  }

  const prioritizedLine =
    normalizedLines.find((line) => keywordRegex.test(line)) ??
    normalizedLines.find((line) => /\d/.test(line)) ??
    normalizedLines[0] ??
    null

  if (!prioritizedLine) {
    return null
  }

  const cleanedCandidate = prioritizedLine
    .replace(
      /^(asiakas(?:tunnus)?|customer(?:\s*reference)?|reference|ref\.?|tunnus)\s*[:#-]?\s*/i,
      ''
    )
    .trim()

  return normalizeCustomerReferenceValue(cleanedCandidate || prioritizedLine)
}

export default function AIAgentChatWidget() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()
  const { t } = useTranslation('installation')
  const { language } = useLanguage()
  const isPageVariant = true
  const defaultQuickReplyPrompts = getDefaultQuickReplyPrompts(isPageVariant, language)
  const welcomeMessage = getWelcomeMessage(isPageVariant, language)
  const installModePrompts = getInstallModePrompts(language)
  const [quickYesLabel, quickNoLabel] = getLocalizedYesNoPrompts(language)
  const startOverPrompt = getLocalizedStartOverPrompt(language)
  const goBackPrompt = getLocalizedGoBackPrompt(language)
  const retryPrompt = getLocalizedRetryPrompt(language)
  const quickPromptSectionTitle = getLocalizedQuickPromptSectionTitle(language)
  const aiInterpretationSectionTitle = getLocalizedQuickPromptSectionTitle(language, 'confirm_ai')
  const assistantTypingLabel = getLocalizedAssistantTypingLabel(language)
  const chatPanelTitle = getLocalizedChatPanelTitle(isPageVariant, language)
  const chatPanelSubtitle = getLocalizedChatPanelSubtitle(isPageVariant, language)
  const projectSelectGuidanceText = t('chatWidgetProjectSelectGuidance')
  const projectSelectLabel = t('chatWidgetProjectSelectLabel')
  const selectActionLabel = t('chatWidgetSelectAction')
  const hideExampleImageLabel = t('chatWidgetHideExampleImage')
  const showExampleImageLabel = t('chatWidgetShowExampleImage')
  const deviceCodeUnderQrHintText = t('chatWidgetDeviceCodeUnderQrHint')
  const noOptionsAvailableHintText = t('chatWidgetNoOptionsAvailableHint')
  const deviceCodeEntryModeHintText = t('chatWidgetDeviceCodeEntryModeHint')
  const manualDeviceEntryInstruction = t('chatWidgetManualDeviceEntryInstruction')
  const enter6DigitCodePromptText = t('chatWidgetEnter6DigitCodePrompt')
  const openChatAriaLabel = t('chatWidgetOpenChatAria')
  const newConversationAriaLabel = t('chatWidgetNewConversationAria')
  const closeChatAriaLabel = t('chatWidgetCloseChatAria')
  const messagePlaceholder = t('chatWidgetMessagePlaceholder')
  const sendMessageAriaLabel = t('chatWidgetSendMessageAria')
  const closeActionLabel = t('chatWidgetCloseAction')
  const selectTagActionLabel = t('chatWidgetSelectTagAction')
  const selectThisTagActionLabel = t('chatWidgetSelectThisTagAction')
  const scanQrCodeActionLabel = t('chatWidgetScanQrCodeAction')
  const bluetoothListActionLabel = t('chatWidgetBluetoothListAction')
  const rssiLabel = t('chatWidgetRssiLabel')
  const orientationLabel = t('chatWidgetOrientationLabel')
  const promptScanDeviceQrCode = t('chatWidgetPromptScanDeviceQrCode')
  const promptScanTagQrCode = t('chatWidgetPromptScanTagQrCode')
  const promptStartBluetoothTagScan = t('chatWidgetPromptStartBluetoothTagScan')
  const promptTryAiInterpretationFromImage = t('chatWidgetPromptTryAiInterpretationFromImage')
  const promptReadFromImage = t('chatWidgetPromptReadFromImage')
  const promptEnterManually = t('chatWidgetPromptEnterManually')
  const promptReadNameplateFromImage = t('chatWidgetPromptReadNameplateFromImage')
  const promptEnterNameManually = t('chatWidgetPromptEnterNameManually')
  const promptEnterManufacturerManually = t('chatWidgetPromptEnterManufacturerManually')
  const promptEnterProductCodeManually = t('chatWidgetPromptEnterProductCodeManually')
  const promptEnterPowerManually = t('chatWidgetPromptEnterPowerManually')
  const promptEnterDetailsManually = t('chatWidgetPromptEnterDetailsManually')
  const promptEnterDeviceDetails = t('chatWidgetPromptEnterDeviceDetails')
  const promptEnter6DigitDeviceCode = t('chatWidgetPromptEnter6DigitDeviceCode')
  const promptShowProjectsAgain = t('chatWidgetPromptShowProjectsAgain')
  const promptUploadDeviceImage = t('chatWidgetPromptUploadDeviceImage')

  const [open, setOpen] = useState(isPageVariant)
  const [input, setInput] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [sessionId, setSessionId] = useState<string | undefined>(undefined)
  const [assistantState, setAssistantState] = useState<AssistantState>(DEFAULT_ASSISTANT_STATE)
  const [assistantOptions, setAssistantOptions] = useState<AssistantOption[]>([])
  const [assistantInputType, setAssistantInputType] = useState<AssistantInputType>('text')
  const [guidedPrompts, setGuidedPrompts] = useState<string[]>(defaultQuickReplyPrompts)
  const [scanSecondsLeft, setScanSecondsLeft] = useState<number | null>(null)
  const [selectedOption, setSelectedOption] = useState('')
  const [previewTagId, setPreviewTagId] = useState<string | null>(null)
  const [scanDialogOpen, setScanDialogOpen] = useState(false)
  const [scanDialogMode, setScanDialogMode] = useState<ScanDialogMode>('device')
  const [scanError, setScanError] = useState<string | null>(null)
  const [isHandlingQrScan, setIsHandlingQrScan] = useState(false)
  const [isReadingDeviceCodeWithAI, setIsReadingDeviceCodeWithAI] = useState(false)
  const [isReadingHeaterLabelWithAI, setIsReadingHeaterLabelWithAI] = useState(false)
  const [showDeviceCodeExampleImage, setShowDeviceCodeExampleImage] = useState(isPageVariant)
  const [isOcrScannerDialogOpen, setIsOcrScannerDialogOpen] = useState(false)
  const [isOcrScannerBusy, setIsOcrScannerBusy] = useState(false)
  const [isDeviceImageUploadDialogOpen, setIsDeviceImageUploadDialogOpen] = useState(false)
  const [deviceImageUploadAssetId, setDeviceImageUploadAssetId] = useState<string | null>(null)
  const [isUploadingDeviceImage, setIsUploadingDeviceImage] = useState(false)
  const [tagSelectionMode, setTagSelectionMode] = useState<TagSelectionMode>('qr')
  const [isInstallModeSelectionPending, setIsInstallModeSelectionPending] = useState(false)
  const [pendingInterpretedDeviceCode, setPendingInterpretedDeviceCode] =
    useState<PendingInterpretedDeviceCode | null>(null)
  const [pendingInterpretedHeaterDetails, setPendingInterpretedHeaterDetails] =
    useState<PendingInterpretedHeaterDetails | null>(null)
  const [pendingInterpretedCustomerReference, setPendingInterpretedCustomerReference] =
    useState<PendingInterpretedCustomerReference | null>(null)
  const [deviceCodeAiStatusMessage, setDeviceCodeAiStatusMessage] = useState<string | null>(null)
  const [heaterLabelAiStatusMessage, setHeaterLabelAiStatusMessage] = useState<string | null>(null)
  const [userCoordinates, setUserCoordinates] = useState<UserCoordinates | null>(null)
  const [hasTriedProjectLocationLookup, setHasTriedProjectLocationLookup] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>(() => [
    {
      id: 'welcome',
      role: 'assistant',
      content: welcomeMessage,
      createdAt: Date.now()
    }
  ])

  const [projectContext, setProjectContext] = useState<
    Array<{
      id: number
      name: string
      companyId: number | null
      lat: number | null
      long: number | null
    }>
  >([])
  const [deviceContext, setDeviceContext] = useState<ChatWidgetDeviceContextItem[]>([])
  const [discoveredTags, setDiscoveredTags] = useState<PairedRuuviTag[]>([])
  const excludedValveTagIdFromDiscoveredList =
    assistantState?.workflow === 'device_install' && assistantState?.step === 'ask_valve_out_tag_id'
      ? assistantState?.valveInRuuviTagId
      : assistantState?.workflow === 'device_install' &&
          assistantState?.step === 'ask_valve_in_tag_id'
        ? assistantState?.valveOutRuuviTagId
        : undefined
  const availableDiscoveredTags = filterDiscoveredTagsExcluding(
    discoveredTags,
    excludedValveTagIdFromDiscoveredList
  )
  const previewTag = previewTagId
    ? (availableDiscoveredTags.find((tag) => tag.id === previewTagId) ?? null)
    : null

  const assistantUiHistoryRef = useRef<AssistantUiSnapshot[]>([
    createAssistantUiSnapshot(DEFAULT_ASSISTANT_STATE, 'text', [], defaultQuickReplyPrompts)
  ])

  const resetAssistantUiHistory = useCallback(() => {
    assistantUiHistoryRef.current = [
      createAssistantUiSnapshot(DEFAULT_ASSISTANT_STATE, 'text', [], defaultQuickReplyPrompts)
    ]
  }, [defaultQuickReplyPrompts])

  const recordAssistantUiSnapshot = useCallback((snapshot: AssistantUiSnapshot) => {
    const normalizedSnapshot = createAssistantUiSnapshot(
      snapshot.state,
      snapshot.inputType,
      snapshot.options,
      snapshot.guidedPrompts
    )
    const history = assistantUiHistoryRef.current
    const previousSnapshot = history[history.length - 1]

    if (!previousSnapshot || !areAssistantUiSnapshotsEqual(previousSnapshot, normalizedSnapshot)) {
      history.push(normalizedSnapshot)
    }
  }, [])

  const resetConversation = () => {
    setSessionId(undefined)
    setAssistantState(DEFAULT_ASSISTANT_STATE)
    setAssistantInputType('text')
    setAssistantOptions([])
    setGuidedPrompts(defaultQuickReplyPrompts)
    setScanSecondsLeft(null)
    setSelectedOption('')
    setPreviewTagId(null)
    setScanDialogOpen(false)
    setScanDialogMode('device')
    setScanError(null)
    setIsHandlingQrScan(false)
    setIsReadingDeviceCodeWithAI(false)
    setIsReadingHeaterLabelWithAI(false)
    setShowDeviceCodeExampleImage(isPageVariant)
    setIsOcrScannerDialogOpen(false)
    setIsOcrScannerBusy(false)
    setIsDeviceImageUploadDialogOpen(false)
    setDeviceImageUploadAssetId(null)
    setIsUploadingDeviceImage(false)
    setTagSelectionMode('qr')
    setIsInstallModeSelectionPending(false)
    setPendingInterpretedDeviceCode(null)
    setPendingInterpretedHeaterDetails(null)
    setPendingInterpretedCustomerReference(null)
    setDeviceCodeAiStatusMessage(null)
    setHeaterLabelAiStatusMessage(null)
    setInput('')
    setDiscoveredTags([])
    autoStartedTagScanDeviceRef.current = null
    pendingTagScanStartDeviceRef.current = null
    valveCustomerReferenceExampleShownRef.current = false
    valveHeaterNameplateExampleShownRef.current = false
    resetAssistantUiHistory()
    setMessages([
      {
        id: 'welcome',
        role: 'assistant',
        content: welcomeMessage,
        createdAt: Date.now()
      }
    ])
  }

  const fallbackOptions: AssistantOption[] =
    assistantInputType === 'project_select'
      ? projectContext.map((project) => ({
          value: String(project.id),
          label: `${project.name} (ID ${project.id})`
        }))
      : assistantInputType === 'device_select'
        ? assistantState?.workflow === 'device_install' &&
          (assistantState?.step === 'confirm_existing_device_installation' ||
            assistantState?.step === 'confirm_use_existing_valve_tags')
          ? []
          : deviceContext.map((device) => ({
              value: device.name,
              label: device.label ?? device.assetName ?? device.deviceCode ?? device.name
            }))
        : []

  const effectiveOptions = assistantOptions.length > 0 ? assistantOptions : fallbackOptions
  const isAiInterpretationQuickReplyMode =
    pendingInterpretedDeviceCode !== null && assistantInputType !== 'device_select'
  const isSpineOrValveSummaryConfirmationStep =
    assistantState?.workflow === 'device_install' &&
    assistantState?.step === 'confirm' &&
    (assistantState?.deviceType === 'SPINE' || assistantState?.deviceType === 'VALVE')
  const quickPromptsBeforeNavigation = isAiInterpretationQuickReplyMode
    ? [quickYesLabel, quickNoLabel]
    : guidedPrompts
  const quickPromptsWithDeviceImageUpload = isSpineOrValveSummaryConfirmationStep
    ? Array.from(new Set([...quickPromptsBeforeNavigation, promptUploadDeviceImage]))
    : quickPromptsBeforeNavigation
  const activeQuickPrompts = appendNavigationPrompts(quickPromptsWithDeviceImageUpload, language)
  const activeQuickPromptsKey = activeQuickPrompts.join('|')
  const isDeviceCodeEntryMode =
    (assistantState?.workflow === 'device_install' &&
      assistantState?.step === 'ask_device' &&
      (assistantState?.deviceType === 'SPINE' || assistantState?.deviceType === 'VALVE')) ||
    (assistantState?.workflow === 'ruuvitag_install' && assistantState?.step === 'ask_device')
  const isRuuviTagHostDeviceSelection =
    assistantState?.workflow === 'ruuvitag_install' && assistantState?.step === 'ask_device'
  const isSpineHostDeviceSelection =
    assistantState?.workflow === 'device_install' &&
    assistantState?.step === 'ask_device' &&
    assistantState?.deviceType === 'SPINE'
  const isHostDeviceSelectionWithoutAi = isRuuviTagHostDeviceSelection || isSpineHostDeviceSelection
  const canUseDeviceCodeAiInterpretation = !isHostDeviceSelectionWithoutAi
  const shouldShowTagQrExampleImage =
    !isSending && assistantInputType === 'tag_select' && tagSelectionMode === 'qr'
  const isValveHeaterDetailsEntryMode =
    assistantState?.workflow === 'device_install' &&
    assistantState?.deviceType === 'VALVE' &&
    assistantState?.step === 'ask_valve_heater_details'
  const isValveCustomerReferenceEntryMode =
    assistantState?.workflow === 'device_install' &&
    assistantState?.deviceType === 'VALVE' &&
    assistantState?.step === 'ask_valve_customer_reference'
  const shouldShowHeaterLabelStatusMessage =
    isValveHeaterDetailsEntryMode || isValveCustomerReferenceEntryMode
  const isInstallProjectSelection =
    assistantInputType === 'project_select' &&
    (assistantState?.workflow === 'ruuvitag_install' ||
      assistantState?.workflow === 'device_install') &&
    assistantState?.step === 'ask_project'
  const isExistingInstallationStrategySelection =
    assistantState?.workflow === 'device_install' &&
    assistantState?.step === 'confirm_existing_device_installation'
  const isValveTagStrategySelection =
    assistantState?.workflow === 'device_install' &&
    assistantState?.step === 'confirm_use_existing_valve_tags'
  const isSpineParentPowerSupplySelection =
    assistantState?.workflow === 'device_install' &&
    assistantState?.step === 'ask_spine_parent_power_supply'
  const isSpecialDeviceSelectStep =
    isExistingInstallationStrategySelection ||
    isValveTagStrategySelection ||
    isSpineParentPowerSupplySelection
  const deviceSelectHintText = isExistingInstallationStrategySelection
    ? t('chatWidgetDeviceSelectHintNextStep')
    : isValveTagStrategySelection
      ? t('chatWidgetDeviceSelectHintTagStrategy')
      : isSpineParentPowerSupplySelection
        ? t('chatWidgetDeviceSelectHintFeedingPowerSupply')
        : t('chatWidgetDeviceSelectHintDefault')
  const deviceSelectInputLabel = isExistingInstallationStrategySelection
    ? t('chatWidgetDeviceSelectInputNextStep')
    : isValveTagStrategySelection
      ? t('chatWidgetDeviceSelectInputTagStrategy')
      : isSpineParentPowerSupplySelection
        ? t('chatWidgetDeviceSelectInputFeedingPowerSupply')
        : t('chatWidgetDeviceSelectInputDevice')
  const canUseDeviceCodeToolsInDeviceSelect =
    assistantInputType === 'device_select' && !isSpecialDeviceSelectStep
  const shouldShowDeviceSelectionExampleImage =
    !isSending &&
    assistantInputType === 'device_select' &&
    !isDeviceCodeEntryMode &&
    !isSpecialDeviceSelectStep &&
    showDeviceCodeExampleImage

  const rankedProjectOptions =
    assistantInputType === 'project_select' && isInstallProjectSelection
      ? rankProjectOptionsByDistance(effectiveOptions, projectContext, userCoordinates)
      : effectiveOptions

  const projectQuickOptions: AssistantOption[] = isInstallProjectSelection
    ? rankedProjectOptions.slice(0, 3)
    : []

  const projectQuickOptionsTitle = userCoordinates
    ? t('chatWidgetProjectQuickOptionsNearest')
    : t('chatWidgetProjectQuickOptionsNoLocation')

  const chatInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
  const deviceCodeImageInputRef = useRef<HTMLInputElement | null>(null)
  const heaterLabelImageInputRef = useRef<HTMLInputElement | null>(null)
  const tagScanPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const tagScanCountdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const tagScanTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tagScanActiveRef = useRef(false)
  const activeTagScanDeviceRef = useRef<string | null>(null)
  const autoStartedTagScanDeviceRef = useRef<string | null>(null)
  const pendingTagScanStartDeviceRef = useRef<string | null>(null)
  const previousAssistantStepRef = useRef<AssistantState['step'] | undefined>(undefined)
  const valveCustomerReferenceExampleShownRef = useRef(false)
  const valveHeaterNameplateExampleShownRef = useRef(false)
  const assistantOptionsScrollDependency =
    assistantInputType === 'tag_select' ? 0 : assistantOptions.length

  const { endRef, scrollToBottom } = useChatViewportScroll({
    open,
    isPageVariant,
    isMobile,
    dependencyValues: [
      messages.length,
      activeQuickPromptsKey,
      assistantInputType,
      assistantOptionsScrollDependency,
      deviceCodeAiStatusMessage,
      heaterLabelAiStatusMessage,
      Boolean(pendingInterpretedDeviceCode),
      Boolean(pendingInterpretedHeaterDetails),
      Boolean(pendingInterpretedCustomerReference),
      isDeviceCodeEntryMode,
      isValveHeaterDetailsEntryMode
    ]
  })

  const focusChatInput = () => {
    chatInputRef.current?.focus()
  }

  const normalizeComparablePromptText = (value: string): string => {
    return value
      .toLocaleLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
  }

  const isShowProjectsAgainRequest = (value: string): boolean => {
    return (
      normalizeComparablePromptText(value) ===
      normalizeComparablePromptText(promptShowProjectsAgain)
    )
  }

  const tryRespondToShowProjectsAgainRequest = (requestMessage: string): boolean => {
    if (!isInstallProjectSelection || !isShowProjectsAgainRequest(requestMessage)) {
      return false
    }

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: requestMessage.trim(),
      createdAt: Date.now()
    }

    const allProjectOptions: AssistantOption[] = projectContext.map((project) => ({
      value: String(project.id),
      label: `${project.name} (ID ${project.id})`
    }))
    const rankedOptions = rankProjectOptionsByDistance(
      allProjectOptions,
      projectContext,
      userCoordinates
    )
    const maxProjectsInChatMessage = 12
    const listedProjectOptions = rankedOptions.slice(0, maxProjectsInChatMessage)
    const remainingProjectCount = rankedOptions.length - listedProjectOptions.length
    const responseLines = [
      projectSelectGuidanceText,
      '',
      ...(listedProjectOptions.length > 0
        ? listedProjectOptions.map((option, index) => `${index + 1}. ${option.label}`)
        : [noOptionsAvailableHintText]),
      ...(remainingProjectCount > 0 ? [`... +${remainingProjectCount}`] : [])
    ]

    setMessages((prev) => [...prev, userMessage])
    setAssistantInputType('project_select')
    setAssistantOptions(rankedOptions)
    setGuidedPrompts(
      buildGuidedPrompts(assistantState, 'project_select', defaultQuickReplyPrompts, language)
    )
    setSelectedOption('')
    setIsInstallModeSelectionPending(false)
    pushAssistantMessage(responseLines.join('\n'))

    return true
  }

  const openOcrScannerDialog = () => {
    if (isSending || isHandlingQrScan || isReadingDeviceCodeWithAI || isReadingHeaterLabelWithAI) {
      return
    }

    if (isHostDeviceSelectionWithoutAi) {
      return
    }

    setScanError(null)
    setIsOcrScannerDialogOpen(true)
  }

  const closeOcrScannerDialog = () => {
    if (isOcrScannerBusy) {
      return
    }

    setIsOcrScannerDialogOpen(false)
  }

  const submitOcrImageFile = async (file: File) => {
    setIsOcrScannerDialogOpen(false)
    await handleDeviceCodeImageUpload(file)
  }

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const projects = await getProjects()
        setProjectContext(
          projects.map((project) => {
            const latitude = Number(project.lat)
            const longitude = Number(project.long)

            return {
              id: project.id,
              name: project.name,
              companyId: project.companyId,
              lat: Number.isFinite(latitude) ? latitude : null,
              long: Number.isFinite(longitude) ? longitude : null
            }
          })
        )
      } catch (_error) {
        setProjectContext([])
      }
    }

    void loadProjects()
  }, [])

  useEffect(() => {
    if (!isInstallProjectSelection || hasTriedProjectLocationLookup || userCoordinates) {
      return
    }

    setHasTriedProjectLocationLookup(true)

    if (!navigator.geolocation) {
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position: GeolocationPosition) => {
        setUserCoordinates({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        })
      },
      () => {
        setUserCoordinates(null)
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 60000
      }
    )
  }, [isInstallProjectSelection, hasTriedProjectLocationLookup, userCoordinates])

  useEffect(() => {
    const projectId = assistantState?.projectId

    if (!projectId) {
      setDeviceContext([])
      return
    }

    const loadDevices = async () => {
      try {
        const devices = await getProjectDevices(projectId)
        setDeviceContext(mapProjectDevicesToChatContext(devices))
      } catch (_error) {
        setDeviceContext([])
      }
    }

    void loadDevices()
  }, [assistantState?.projectId])

  const pushAssistantMessage = (
    content: string,
    options?: {
      kind?: ChatMessageKind
      decisionType?: ChatDecisionType
    }
  ) => {
    const assistantMessage: ChatMessage = {
      id: `assistant-${Date.now()}`,
      role: 'assistant',
      content,
      createdAt: Date.now(),
      kind: options?.kind ?? 'text',
      decisionType: options?.decisionType
    }

    setMessages((prev) => [...prev, assistantMessage])
  }

  const pushValveCustomerReferenceExampleMessage = () => {
    pushAssistantMessage('', {
      kind: 'valve_customer_reference_example'
    })
  }

  const pushValveHeaterNameplateExampleMessage = () => {
    pushAssistantMessage('', {
      kind: 'valve_heater_nameplate_example'
    })
  }

  const stopTagScan = useCallback(() => {
    tagScanActiveRef.current = false
    activeTagScanDeviceRef.current = null

    if (tagScanPollIntervalRef.current) {
      clearInterval(tagScanPollIntervalRef.current)
      tagScanPollIntervalRef.current = null
    }

    if (tagScanCountdownIntervalRef.current) {
      clearInterval(tagScanCountdownIntervalRef.current)
      tagScanCountdownIntervalRef.current = null
    }

    if (tagScanTimeoutRef.current) {
      clearTimeout(tagScanTimeoutRef.current)
      tagScanTimeoutRef.current = null
    }

    setScanSecondsLeft((previous) => (previous === null ? previous : null))
  }, [])

  useEffect(() => {
    return () => {
      stopTagScan()
    }
  }, [stopTagScan])

  const goBackOneAssistantStep = () => {
    if (assistantUiHistoryRef.current.length <= 1) {
      return
    }

    assistantUiHistoryRef.current.pop()

    const previousSnapshot = assistantUiHistoryRef.current[assistantUiHistoryRef.current.length - 1]

    if (!previousSnapshot) {
      return
    }

    setAssistantState({ ...previousSnapshot.state })
    setAssistantInputType(previousSnapshot.inputType)
    setAssistantOptions(previousSnapshot.options.map((option) => ({ ...option })))
    setGuidedPrompts([...previousSnapshot.guidedPrompts])
    setSelectedOption('')
    setIsInstallModeSelectionPending(false)
    setPendingInterpretedDeviceCode(null)
    setPendingInterpretedHeaterDetails(null)
    setPendingInterpretedCustomerReference(null)
    setDeviceCodeAiStatusMessage(null)
    setHeaterLabelAiStatusMessage(null)

    if (previousSnapshot.inputType !== 'tag_select') {
      stopTagScan()
    }
  }

  const buildContext = (): AssistantContext => ({
    language,
    projects: projectContext,
    devices: deviceContext,
    discoveredTags: discoveredTags.map((tag) => ({
      id: tag.id,
      rssi: tag.latest?.condition?.rssi ?? tag.latest?.acceleration?.rssi ?? null
    }))
  })

  const resolveAssistantDeviceAssetId = async (): Promise<string | null> => {
    const assistantDeviceName = assistantState?.deviceName?.trim()

    if (!assistantDeviceName) {
      return null
    }

    const normalizedAssistantDeviceName = assistantDeviceName.toLowerCase()
    const matchingDeviceFromContext = deviceContext.find(
      (device) => device.name.trim().toLowerCase() === normalizedAssistantDeviceName
    )

    if (matchingDeviceFromContext?.assetId) {
      return matchingDeviceFromContext.assetId
    }

    try {
      const fetchedDevice = await getDeviceByName(assistantDeviceName)

      return fetchedDevice.asset?.id ?? fetchedDevice.assetId ?? null
    } catch (_error) {
      return null
    }
  }

  const openDeviceImageUploadDialog = async () => {
    if (isSending || isUploadingDeviceImage) {
      return
    }

    const resolvedAssetId = await resolveAssistantDeviceAssetId()

    if (!resolvedAssetId) {
      pushAssistantMessage(t('chatWidgetDeviceImageUploadMissingAsset'))
      return
    }

    setDeviceImageUploadAssetId(resolvedAssetId)
    setIsDeviceImageUploadDialogOpen(true)
  }

  const closeDeviceImageUploadDialog = () => {
    if (isUploadingDeviceImage) {
      return
    }

    setIsDeviceImageUploadDialogOpen(false)
    setDeviceImageUploadAssetId(null)
  }

  const handleTagCardSelect = async (tagId: string) => {
    if (isSending) {
      return
    }

    stopTagScan()

    await processUserMessage(tagId, {
      selectedFromList: true,
      inputType: 'tag_select'
    })
  }

  const startRuuviTagBluetoothScan = async (
    deviceName: string,
    options: {
      announceStartMessage?: boolean
      notifyProgressMessages?: boolean
      resetDiscoveredTags?: boolean
    } = {}
  ) => {
    const {
      announceStartMessage = true,
      notifyProgressMessages = true,
      resetDiscoveredTags = true
    } = options

    if (
      (tagScanActiveRef.current && activeTagScanDeviceRef.current === deviceName) ||
      pendingTagScanStartDeviceRef.current === deviceName
    ) {
      return
    }

    pendingTagScanStartDeviceRef.current = deviceName

    try {
      stopTagScan()
      setAssistantState((previousState) => ({
        ...previousState,
        workflow: previousState?.workflow,
        step: previousState?.workflow === 'ruuvitag_install' ? 'ask_tag_id' : previousState?.step,
        deviceName
      }))

      if (announceStartMessage) {
        pushAssistantMessage(t('chatWidgetStartingBluetoothScanForDevice', { deviceName }))
      }

      await invokeAction(deviceName, { ruuvi_pairing: 60 })
      pendingTagScanStartDeviceRef.current = null
      tagScanActiveRef.current = true
      activeTagScanDeviceRef.current = deviceName
      autoStartedTagScanDeviceRef.current = deviceName
      setScanSecondsLeft(60)

      if (resetDiscoveredTags) {
        setDiscoveredTags([])
      }

      const discoveredMap = new Map<string, PairedRuuviTag>()
      let hasNotifiedFoundTags = false

      await new Promise<void>((resolve) => {
        const TWO_SECONDS = 2 * 1000
        const ONE_SECOND = 1000
        const ONE_MINUTE = 60 * 1000

        const pollTags = async () => {
          if (!tagScanActiveRef.current) {
            return
          }

          try {
            const tags = await getPairedRuuviTags(deviceName)

            if (!Array.isArray(tags) || tags.length === 0) {
              return
            }

            tags.forEach((incomingRaw) => {
              const incoming = incomingRaw as PairedRuuviTag
              const existing = discoveredMap.get(incoming.id)

              discoveredMap.set(incoming.id, {
                ...(existing ?? incoming),
                ...incoming,
                latest: incoming.latest ?? existing?.latest
              })
            })

            const normalizedTags = Array.from(discoveredMap.values())

            setDiscoveredTags(normalizedTags)
            setAssistantInputType('tag_select')
            setAssistantOptions(
              normalizedTags.map((tag) => ({
                value: tag.id,
                label:
                  tag.latest?.condition?.rssi !== null && tag.latest?.condition?.rssi !== undefined
                    ? `${tag.id} (RSSI ${tag.latest.condition.rssi})`
                    : tag.id
              }))
            )

            if (!hasNotifiedFoundTags && notifyProgressMessages) {
              hasNotifiedFoundTags = true
              pushAssistantMessage(t('chatWidgetFoundTagsContinuingScan'))
            }
          } catch (_pollError) {
            // Keep polling even if one fetch fails.
          }
        }

        const interval = setInterval(() => {
          void pollTags()
        }, TWO_SECONDS)
        tagScanPollIntervalRef.current = interval

        const countdownInterval = setInterval(() => {
          if (!tagScanActiveRef.current) {
            return
          }

          setScanSecondsLeft((previous) => {
            if (previous === null) {
              return null
            }

            return Math.max(previous - 1, 0)
          })
        }, ONE_SECOND)
        tagScanCountdownIntervalRef.current = countdownInterval

        void pollTags()

        const timeout = setTimeout(() => {
          if (!tagScanActiveRef.current) {
            resolve()
            return
          }

          stopTagScan()

          if (notifyProgressMessages) {
            if (discoveredMap.size === 0) {
              pushAssistantMessage(t('chatWidgetNoTagsFoundYet'))
            } else {
              pushAssistantMessage(
                t('chatWidgetBluetoothScanCompletedFoundTags', { count: discoveredMap.size })
              )
            }
          }

          clearTimeout(timeout)
          resolve()
        }, ONE_MINUTE)
        tagScanTimeoutRef.current = timeout
      })
    } finally {
      if (pendingTagScanStartDeviceRef.current === deviceName) {
        pendingTagScanStartDeviceRef.current = null
      }
    }
  }

  useEffect(() => {
    const currentStep = assistantState?.step
    const isTagStep =
      currentStep === 'ask_tag_id' ||
      currentStep === 'ask_valve_in_tag_id' ||
      currentStep === 'ask_valve_out_tag_id'

    if (isTagStep && currentStep !== previousAssistantStepRef.current) {
      // Keep QR view as the default; user explicitly opts into Bluetooth list.
      setTagSelectionMode('qr')
    }

    previousAssistantStepRef.current = currentStep
  }, [assistantState?.step])

  useEffect(() => {
    const isRuuviTagTagStep =
      assistantState?.workflow === 'ruuvitag_install' &&
      (assistantState?.step === 'ask_tag_id' || assistantInputType === 'tag_select')
    const isValveTagStep =
      assistantState?.workflow === 'device_install' &&
      (assistantState?.step === 'ask_valve_in_tag_id' ||
        assistantState?.step === 'ask_valve_out_tag_id')
    const shouldAutoStartBackgroundTagScan = isRuuviTagTagStep || isValveTagStep

    if (!shouldAutoStartBackgroundTagScan) {
      stopTagScan()
      autoStartedTagScanDeviceRef.current = null
      return
    }

    const deviceName = assistantState?.deviceName

    if (!deviceName) {
      return
    }

    if (
      autoStartedTagScanDeviceRef.current === deviceName ||
      (tagScanActiveRef.current && activeTagScanDeviceRef.current === deviceName)
    ) {
      return
    }

    void startRuuviTagBluetoothScan(deviceName, {
      announceStartMessage: false,
      notifyProgressMessages: false,
      resetDiscoveredTags: false
    }).catch(() => {
      // Keep manual and QR flow available even if background scan fails.
    })
  }, [
    stopTagScan,
    assistantInputType,
    assistantState?.deviceName,
    assistantState?.step,
    assistantState?.workflow
  ])

  const processAssistantAction = async (action?: {
    type?: 'none' | 'start_ruuvitag_scan' | 'create_ruuvitag'
    payload?: {
      projectId?: number
      companyId?: number | null
      deviceName?: string
      ruuviTagId?: string
      assetName?: string
    }
  }) => {
    if (!action || action.type === 'none') {
      return
    }

    if (action.type !== 'start_ruuvitag_scan' && action.type !== 'create_ruuvitag') {
      return
    }

    if (action.type === 'start_ruuvitag_scan') {
      const deviceName = action.payload?.deviceName

      if (!deviceName) {
        pushAssistantMessage(t('chatWidgetScanCouldNotStartMissingDeviceName'))
        return
      }

      try {
        await startRuuviTagBluetoothScan(deviceName, {
          announceStartMessage: true,
          notifyProgressMessages: true,
          resetDiscoveredTags: true
        })
      } catch (_error) {
        stopTagScan()
        pushAssistantMessage(t('chatWidgetBluetoothScanStartFailedEnterTagManually'))
      }

      return
    }

    const payload = action.payload

    if (
      !payload?.projectId ||
      !payload?.deviceName ||
      !payload?.ruuviTagId ||
      !payload?.assetName
    ) {
      pushAssistantMessage(t('chatWidgetInstallCouldNotSaveMissingData'))
      return
    }

    try {
      const created = await createOrUpdateRuuviTag({
        id: payload.ruuviTagId,
        device: payload.deviceName,
        asset: {
          name: payload.assetName,
          description: '',
          company: payload.companyId ?? null,
          project: payload.projectId,
          level: null,
          lift: null
        }
      })

      pushAssistantMessage(
        [
          t('chatWidgetInstallSavedSuccess'),
          t('chatWidgetRuuviTagLabelValue', { id: created.id }),
          t('chatWidgetDeviceLabelValue', {
            deviceName: created.deviceName ?? payload.deviceName
          })
        ].join('\n')
      )

      setAssistantState({
        workflow: 'none',
        step: 'idle'
      })
      setAssistantInputType('text')
      setAssistantOptions([])
    } catch (_error) {
      pushAssistantMessage(t('chatWidgetInstallSaveFailedCheckAndRetry'))
    }
  }

  const processUserMessage = async (
    text: string,
    selection?: {
      selectedFromList?: boolean
      inputType?: 'text' | 'project_select' | 'device_select' | 'tag_select' | 'confirm'
    },
    contextOverride?: Partial<AssistantContext>
  ) => {
    const trimmed = text.trim()

    if (!trimmed || isSending) {
      return
    }

    if (!isPageVariant && isInstallModeSelectionPending) {
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: trimmed,
        createdAt: Date.now()
      }

      setMessages((prev) => [...prev, userMessage])
      setInput('')
      setSelectedOption('')

      const modeChoice = detectInstallModeChoice(trimmed)

      if (modeChoice === 'automatic') {
        setIsInstallModeSelectionPending(false)
        setGuidedPrompts(defaultQuickReplyPrompts)
        setAssistantInputType('text')
        setAssistantOptions([])
        setOpen(false)
        navigate('/ai-agent?source=chat-widget')
        return
      }

      if (modeChoice === 'manual') {
        setIsInstallModeSelectionPending(false)
        setGuidedPrompts(defaultQuickReplyPrompts)
        setAssistantInputType('text')
        setAssistantOptions([])
        setOpen(false)
        navigate('/install?source=chat-widget')
        return
      }

      if (modeChoice === 'cancel') {
        setIsInstallModeSelectionPending(false)
        setGuidedPrompts(defaultQuickReplyPrompts)
        setAssistantInputType('text')
        setAssistantOptions([])
        pushAssistantMessage(t('chatWidgetInstallModeSelectionCanceled'))
        return
      }

      setGuidedPrompts(installModePrompts)
      pushAssistantMessage(t('chatWidgetChooseInstallModeToContinue'))
      return
    }

    if (!isPageVariant && !selection?.selectedFromList) {
      const installShortcut = detectFloatingInstallShortcut(trimmed, language)

      if (installShortcut === 'open_ai_agent') {
        const userMessage: ChatMessage = {
          id: `user-${Date.now()}`,
          role: 'user',
          content: trimmed,
          createdAt: Date.now()
        }

        setMessages((prev) => [...prev, userMessage])
        setInput('')
        setSelectedOption('')
        setSessionId(undefined)
        setAssistantState({
          workflow: 'none',
          step: 'idle'
        })
        setIsInstallModeSelectionPending(false)
        setAssistantInputType('text')
        setAssistantOptions([])
        setGuidedPrompts(defaultQuickReplyPrompts)
        setOpen(false)
        navigate('/ai-agent?source=chat-widget')
        return
      }

      if (installShortcut === 'open_manual') {
        const userMessage: ChatMessage = {
          id: `user-${Date.now()}`,
          role: 'user',
          content: trimmed,
          createdAt: Date.now()
        }

        setMessages((prev) => [...prev, userMessage])
        setInput('')
        setSelectedOption('')
        setSessionId(undefined)
        setAssistantState({
          workflow: 'none',
          step: 'idle'
        })
        setIsInstallModeSelectionPending(false)
        setAssistantInputType('text')
        setAssistantOptions([])
        setGuidedPrompts(defaultQuickReplyPrompts)
        setOpen(false)
        navigate('/install?source=chat-widget')
        return
      }
    }

    const shouldPromptInstallModeSelection =
      !isPageVariant && !isInstallModeSelectionPending && isInstallationReference(trimmed)

    if (shouldPromptInstallModeSelection) {
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: trimmed,
        createdAt: Date.now()
      }

      setMessages((prev) => [...prev, userMessage])
      setInput('')
      setSelectedOption('')
      setSessionId(undefined)
      setAssistantState({
        workflow: 'none',
        step: 'idle'
      })
      setIsInstallModeSelectionPending(true)
      setAssistantInputType('text')
      setAssistantOptions([])
      setGuidedPrompts(installModePrompts)

      pushAssistantMessage(t('chatWidgetFloatingInstallModeRoutingNotice'))
      return
    }

    if (!isPageVariant && !selection?.selectedFromList) {
      const localHelpReply = buildFloatingHelpReply(trimmed, language)

      if (localHelpReply) {
        const userMessage: ChatMessage = {
          id: `user-${Date.now()}`,
          role: 'user',
          content: trimmed,
          createdAt: Date.now()
        }

        setMessages((prev) => [...prev, userMessage])
        setInput('')
        setSelectedOption('')
        setAssistantInputType('text')
        setAssistantOptions([])
        setGuidedPrompts(defaultQuickReplyPrompts)
        pushAssistantMessage(localHelpReply)
        return
      }
    }

    const isDeviceSelectionContext =
      assistantInputType === 'device_select' ||
      selection?.inputType === 'device_select' ||
      (assistantState?.workflow === 'ruuvitag_install' && assistantState?.step === 'ask_device')

    if (
      isPageVariant &&
      !selection?.selectedFromList &&
      isDeviceSelectionContext &&
      isCannotFindDeviceMessage(trimmed)
    ) {
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: trimmed,
        createdAt: Date.now()
      }

      setMessages((prev) => [...prev, userMessage])
      setInput('')
      setSelectedOption('')
      setAssistantInputType('device_select')
      setAssistantOptions([])
      setGuidedPrompts([startOverPrompt])
      pushAssistantMessage(buildDeviceNotFoundReply(deviceContext.length, language))
      return
    }

    setPendingInterpretedDeviceCode(null)
    setPendingInterpretedHeaterDetails(null)
    setPendingInterpretedCustomerReference(null)
    setDeviceCodeAiStatusMessage(null)
    setHeaterLabelAiStatusMessage(null)

    if (
      selection?.inputType === 'tag_select' ||
      (assistantInputType === 'tag_select' && scanSecondsLeft !== null)
    ) {
      stopTagScan()
    }

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: trimmed,
      createdAt: Date.now()
    }

    const history = [...messages, userMessage].map((entry) => ({
      role: entry.role,
      content: entry.content
    }))

    setMessages((prev) => [...prev, userMessage])
    setInput('')
    setSelectedOption('')
    setIsSending(true)

    const requestContext: AssistantContext = {
      ...buildContext(),
      ...(contextOverride ?? {})
    }

    try {
      const response = await chatWithAssistant({
        message: trimmed,
        sessionId,
        history,
        state: assistantState,
        context: requestContext,
        selection
      })

      if (response.sessionId) {
        setSessionId(response.sessionId)
      }

      setIsInstallModeSelectionPending(false)

      const assistant = response.assistant
      const incomingState = assistant?.state
      const nextState: AssistantState = incomingState ?? assistantState

      const rawReply = assistant?.reply || response.message || t('chatWidgetNoResponseTryAgain')
      const recoveredReply = extractReplyFromJsonLike(rawReply)

      let replyText = normalizeAssistantReply(recoveredReply ?? rawReply)
      let resolvedState: AssistantState = nextState
      let resolvedInputType: AssistantInputType = assistantInputType
      let resolvedOptions: AssistantOption[] = assistant?.ui?.options ?? []
      let resolvedGuidedPrompts: string[] = defaultQuickReplyPrompts

      if (!isPageVariant && isTooGeneralRequestReply(replyText)) {
        replyText = t('chatWidgetGeneralRequestClarification')
        resolvedInputType = 'text'
        resolvedOptions = []
        resolvedGuidedPrompts = defaultQuickReplyPrompts
      }

      if (!isPageVariant && isInstallModeQuestionReply(replyText)) {
        setIsInstallModeSelectionPending(true)
        resolvedInputType = 'text'
        resolvedOptions = []
        resolvedGuidedPrompts = installModePrompts
        replyText = t('chatWidgetChooseInstallModeToContinue')
      }

      if (isPageVariant && isInstallModeQuestionReply(replyText)) {
        const forcedState: AssistantState = {
          ...(nextState ?? {}),
          workflow: 'ruuvitag_install',
          step: 'ask_project'
        }
        const projectOptions = projectContext.map((project) => ({
          value: String(project.id),
          label: `${project.name} (ID ${project.id})`
        }))
        const forcedPrompts = buildGuidedPrompts(
          forcedState,
          'project_select',
          defaultQuickReplyPrompts,
          language
        )

        replyText = t('chatWidgetRuuvitagInstallSelectProject')
        resolvedState = forcedState
        resolvedInputType = 'project_select'
        resolvedOptions = projectOptions
        resolvedGuidedPrompts = forcedPrompts
      } else {
        const hasAvailableDeviceChoices =
          (assistant?.ui?.inputType === 'device_select' &&
            (assistant?.ui?.options?.length ?? 0) > 0) ||
          deviceContext.length > 0

        const nextInputType = inferAssistantInputType(
          nextState,
          assistant?.ui?.inputType,
          replyText,
          hasAvailableDeviceChoices
        )
        const defaultPrompts = buildGuidedPrompts(
          nextState,
          nextInputType,
          defaultQuickReplyPrompts,
          language
        )
        const shouldPreferTranslationPromptSet =
          nextState?.workflow === 'device_install' &&
          (['ask_spine_level', 'ask_spine_lift', 'edit_valve_level', 'edit_valve_lift'].includes(
            nextState?.step ?? ''
          ) ||
            (nextState?.step === 'confirm' && nextState?.deviceType !== 'VALVE'))
        const backendOptionPrompts =
          nextInputType === 'text' &&
          !shouldPreferTranslationPromptSet &&
          (assistant?.ui?.options?.length ?? 0) > 0 &&
          (assistant?.ui?.options?.length ?? 0) <= 6
            ? Array.from(
                new Set(
                  (assistant?.ui?.options ?? [])
                    .map((option) => option.label.trim())
                    .filter((label) => label.length > 0)
                )
              )
            : null
        const spineNamePromptsFromReply =
          nextState?.workflow === 'device_install' &&
          nextState?.step === 'ask_spine_name' &&
          nextInputType === 'text'
            ? buildSpineNameQuickPromptsFromReply(replyText)
            : null
        const mergedSpineNamePrompts =
          spineNamePromptsFromReply &&
          nextState?.workflow === 'device_install' &&
          nextState?.step === 'ask_spine_name' &&
          nextInputType === 'text'
            ? Array.from(new Set([...spineNamePromptsFromReply, ...defaultPrompts]))
            : null

        resolvedInputType = nextInputType
        resolvedOptions = assistant?.ui?.options ?? []
        resolvedGuidedPrompts = mergedSpineNamePrompts ?? backendOptionPrompts ?? defaultPrompts
      }

      setAssistantState(resolvedState)
      setAssistantInputType(resolvedInputType)
      setAssistantOptions(resolvedOptions)
      setGuidedPrompts(resolvedGuidedPrompts)

      const isValveTagInputSubmission =
        selection?.inputType === 'tag_select' &&
        assistantState?.workflow === 'device_install' &&
        (assistantState?.step === 'ask_valve_in_tag_id' ||
          assistantState?.step === 'ask_valve_out_tag_id')
      const stayedOnSameValveTagStep =
        resolvedState?.workflow === 'device_install' &&
        resolvedState?.step === assistantState?.step &&
        (resolvedState?.step === 'ask_valve_in_tag_id' ||
          resolvedState?.step === 'ask_valve_out_tag_id')

      if (isValveTagInputSubmission && stayedOnSameValveTagStep) {
        setTagSelectionMode('qr')
        setPreviewTagId(null)
      }

      recordAssistantUiSnapshot(
        createAssistantUiSnapshot(
          resolvedState,
          resolvedInputType,
          resolvedOptions,
          resolvedGuidedPrompts
        )
      )

      if (assistant?.ui?.inputType === 'project_select') {
        const normalizedReply = replyText.toLowerCase()
        const hasGuidance =
          normalizedReply.includes('valitse projekti alla olevasta listasta') ||
          normalizedReply.includes(projectSelectGuidanceText.toLowerCase()) ||
          normalizedReply.includes('select a project from the list below') ||
          (normalizedReply.includes('kirjoita') && normalizedReply.includes('chattiin')) ||
          (normalizedReply.includes('type') && normalizedReply.includes('chat'))

        if (!hasGuidance) {
          replyText = `${replyText}\n\n${projectSelectGuidanceText}`
        }
      }

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: replyText,
        createdAt: Date.now()
      }
      setMessages((prev) => [...prev, assistantMessage])

      const resolvedNextWorkflow = resolvedState?.workflow ?? assistantState?.workflow
      const resolvedNextStep = resolvedState?.step ?? assistantState?.step
      const resolvedNextDeviceType = resolvedState?.deviceType ?? assistantState?.deviceType
      const shouldShowValveHeaterNameplateExample =
        resolvedNextWorkflow === 'device_install' &&
        resolvedNextDeviceType === 'VALVE' &&
        resolvedNextStep === 'ask_valve_heater_details'
      const shouldShowValveCustomerReferenceExample =
        isPageVariant &&
        resolvedNextWorkflow === 'device_install' &&
        resolvedNextDeviceType === 'VALVE' &&
        resolvedNextStep === 'ask_valve_customer_reference'

      if (shouldShowValveHeaterNameplateExample) {
        if (!valveHeaterNameplateExampleShownRef.current) {
          valveHeaterNameplateExampleShownRef.current = true
          pushValveHeaterNameplateExampleMessage()
        }
      } else {
        valveHeaterNameplateExampleShownRef.current = false
      }

      if (shouldShowValveCustomerReferenceExample) {
        if (!valveCustomerReferenceExampleShownRef.current) {
          valveCustomerReferenceExampleShownRef.current = true
          pushValveCustomerReferenceExampleMessage()
        }
      } else {
        valveCustomerReferenceExampleShownRef.current = false
      }

      void processAssistantAction(assistant?.action)
    } catch (_error) {
      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: t('chatWidgetAiAgentUnavailableNow'),
        createdAt: Date.now()
      }
      setMessages((prev) => [...prev, assistantMessage])
      setGuidedPrompts([retryPrompt, startOverPrompt])
    } finally {
      setIsSending(false)
    }
  }

  const sendMessage = async () => {
    const trimmedInput = input.trim()

    if (!trimmedInput) {
      return
    }

    if (pendingInterpretedDeviceCode) {
      const yesNoDecision = parseYesNoAnswer(trimmedInput)

      setInput('')

      if (yesNoDecision === null) {
        setDeviceCodeAiStatusMessage(
          t('chatWidgetAiAnswerYesNoToContinue', {
            yes: quickYesLabel,
            no: quickNoLabel
          })
        )
        return
      }

      await handleInterpretedDeviceCodeDecision(yesNoDecision)
      return
    }

    if (pendingInterpretedHeaterDetails) {
      const yesNoDecision = parseYesNoAnswer(trimmedInput)

      setInput('')

      if (yesNoDecision === null) {
        setHeaterLabelAiStatusMessage(
          t('chatWidgetAiAnswerYesNoToContinue', {
            yes: quickYesLabel,
            no: quickNoLabel
          })
        )
        return
      }

      await handleInterpretedHeaterDetailsDecision(yesNoDecision)
      return
    }

    if (pendingInterpretedCustomerReference) {
      const yesNoDecision = parseYesNoAnswer(trimmedInput)

      setInput('')

      if (yesNoDecision === null) {
        pushAssistantMessage(
          t('chatWidgetAiAnswerYesNoToContinue', {
            yes: quickYesLabel,
            no: quickNoLabel
          })
        )
        return
      }

      await handleInterpretedCustomerReferenceDecision(yesNoDecision)
      return
    }

    if (assistantInputType === 'project_select') {
      if (tryRespondToShowProjectsAgainRequest(trimmedInput)) {
        setInput('')
        return
      }

      const typedProjectId = Number(trimmedInput)

      if (
        Number.isFinite(typedProjectId) &&
        projectContext.some((project) => project.id === typedProjectId)
      ) {
        await submitAssistantOption(String(typedProjectId), 'project_select')
        return
      }

      const matchedProject = findProjectByTypedName(projectContext, trimmedInput)

      if (matchedProject) {
        await submitAssistantOption(String(matchedProject.id), 'project_select')
        return
      }

      await processUserMessage(trimmedInput, {
        selectedFromList: false,
        inputType: 'project_select'
      })
      return
    }

    if (assistantInputType === 'device_select') {
      await processUserMessage(trimmedInput, {
        selectedFromList: false,
        inputType: 'device_select'
      })
      return
    }

    if (assistantInputType === 'tag_select') {
      await processUserMessage(trimmedInput, {
        selectedFromList: false,
        inputType: 'tag_select'
      })
      return
    }

    if (assistantInputType === 'confirm') {
      await processUserMessage(trimmedInput, {
        selectedFromList: true,
        inputType: 'confirm'
      })
      return
    }

    await processUserMessage(trimmedInput)
  }

  const resolveDeviceFromScannedCode = (code: string) => {
    return deviceContext.find((device) => {
      const shortUuidMatch = device.shortUUID
        ? extractCompleteShortCode(device.shortUUID) === code
        : false
      const deviceCodeMatch = device.deviceCode
        ? extractCompleteShortCode(device.deviceCode) === code
        : false

      return shortUuidMatch || deviceCodeMatch
    })
  }

  const handleQrScanResult = async (rawValue: string) => {
    if (isSending || isHandlingQrScan) {
      return
    }

    if (scanDialogMode === 'tag') {
      const scannedTagId = extractRuuviTagIdFromQr(rawValue)

      if (!scannedTagId) {
        setScanError(t('chatWidgetTagQrIdNotFoundError'))
        return
      }

      setIsHandlingQrScan(true)
      setScanError(null)
      setScanDialogOpen(false)

      await processUserMessage(scannedTagId, {
        selectedFromList: false,
        inputType: 'tag_select'
      })

      setIsHandlingQrScan(false)
      return
    }

    const scannedCode = extractCompleteShortCode(rawValue)

    if (!scannedCode) {
      return
    }

    setIsHandlingQrScan(true)
    setScanError(null)
    setScanDialogOpen(false)

    const matchedDevice = resolveDeviceFromScannedCode(scannedCode)
    const valueToSend = matchedDevice?.name ?? scannedCode

    if (!matchedDevice) {
      pushAssistantMessage(t('chatWidgetScannedDeviceCodeForwardedToBot', { code: scannedCode }))
    }

    await processUserMessage(valueToSend, {
      selectedFromList: Boolean(matchedDevice),
      inputType: 'device_select'
    })

    setIsHandlingQrScan(false)
  }

  const handleDeviceCodeImageUpload = async (file: File) => {
    if (isSending || isHandlingQrScan || isReadingDeviceCodeWithAI || isReadingHeaterLabelWithAI) {
      return
    }

    setPendingInterpretedDeviceCode(null)
    setDeviceCodeAiStatusMessage(t('chatWidgetAnalyzingImage'))
    setScanError(null)
    setIsReadingDeviceCodeWithAI(true)

    try {
      const result = await readDeviceCodeWithAI(file)
      const interpretedCode = result.deviceCode ? extractCompleteShortCode(result.deviceCode) : null

      if (!interpretedCode) {
        setDeviceCodeAiStatusMessage(t('chatWidgetAiNoReliableDeviceCodeTryAnotherImage'))
        return
      }

      const matchedDevice = resolveDeviceFromScannedCode(interpretedCode)

      if (matchedDevice) {
        const deviceLabel = matchedDevice.label ?? matchedDevice.assetName ?? matchedDevice.name
        setDeviceCodeAiStatusMessage(
          t('chatWidgetAiUseMatchedDevicePrompt', {
            deviceLabel,
            yes: quickYesLabel,
            no: quickNoLabel
          })
        )
      } else {
        setDeviceCodeAiStatusMessage(
          t('chatWidgetAiUseInterpretedDeviceCodePrompt', {
            code: interpretedCode,
            yes: quickYesLabel,
            no: quickNoLabel
          })
        )
      }

      setPendingInterpretedDeviceCode({
        code: interpretedCode,
        matchedDeviceName: matchedDevice?.name
      })
    } catch (_error) {
      setDeviceCodeAiStatusMessage(t('chatWidgetAiDeviceCodeInterpretationFailed'))
    } finally {
      setIsReadingDeviceCodeWithAI(false)
    }
  }

  const handleHeaterLabelImageUpload = async (file: File) => {
    if (isSending || isHandlingQrScan || isReadingDeviceCodeWithAI || isReadingHeaterLabelWithAI) {
      return
    }

    const isValveCustomerReferenceStep =
      assistantState?.workflow === 'device_install' &&
      assistantState?.deviceType === 'VALVE' &&
      assistantState?.step === 'ask_valve_customer_reference'

    setPendingInterpretedHeaterDetails(null)
    setPendingInterpretedCustomerReference(null)
    if (isValveCustomerReferenceStep) {
      setHeaterLabelAiStatusMessage(null)
      pushAssistantMessage(t('chatWidgetAnalyzingCustomerReferenceFromImage'))
    } else {
      setHeaterLabelAiStatusMessage(null)
      pushAssistantMessage(t('chatWidgetAnalyzingHeaterNameplate'))
    }
    setScanError(null)
    setIsReadingHeaterLabelWithAI(true)

    try {
      if (isValveCustomerReferenceStep) {
        const deviceCodeResult = await readDeviceCodeWithAI(file)
        const interpretedCode = deviceCodeResult.deviceCode
          ? extractCompleteShortCode(deviceCodeResult.deviceCode)
          : null
        const normalizedInterpretedCode = normalizeCustomerReferenceValue(interpretedCode)

        if (normalizedInterpretedCode) {
          setPendingInterpretedCustomerReference({
            value: normalizedInterpretedCode
          })
          setHeaterLabelAiStatusMessage(null)
          pushAssistantMessage(
            t('chatWidgetAiInterpretedCustomerReferenceConfirm', {
              value: normalizedInterpretedCode
            }),
            {
              decisionType: 'customer_reference'
            }
          )
          return
        }

        const heaterResult = await readHeaterLabelWithAI(file)
        const customerReferenceCandidate = extractBestCustomerReferenceCandidate(
          heaterResult.textLines
        )
        const normalizedCustomerReference = normalizeCustomerReferenceValue(
          customerReferenceCandidate
        )

        if (!normalizedCustomerReference) {
          setHeaterLabelAiStatusMessage(null)
          pushAssistantMessage(t('chatWidgetAiNoCustomerReferenceFoundInImage'))
          return
        }

        setHeaterLabelAiStatusMessage(null)
        setPendingInterpretedCustomerReference({
          value: normalizedCustomerReference
        })
        pushAssistantMessage(
          t('chatWidgetAiInterpretedCustomerReferenceConfirm', {
            value: normalizedCustomerReference
          }),
          {
            decisionType: 'customer_reference'
          }
        )
        return
      }

      const result = await readHeaterLabelWithAI(file)

      const parsedTypeCode = result.parsed?.typeCode?.trim() ?? ''
      const parsedSerialNumber = result.parsed?.serialNumber?.trim() ?? ''
      const parsedFamilyLabel = result.parsed?.familyLabel ?? null
      const parsedHeatingPower = result.parsed?.heatingPowerKw ?? null
      const parsedHeatingMediumLabel = result.parsed?.heatingMediumLabel ?? null
      const parsedFanControlLabel = result.parsed?.fanControlLabel ?? null
      const matchedProduct = result.catalogProduct

      const detailsParts = [
        parsedTypeCode ? t('chatWidgetHeaterDetailTypeCode', { typeCode: parsedTypeCode }) : null,
        parsedSerialNumber ? `${t('heater:serialNumber')}: ${parsedSerialNumber}` : null,
        parsedFamilyLabel
          ? t('chatWidgetHeaterDetailFamilyLabel', { familyLabel: parsedFamilyLabel })
          : null,
        parsedHeatingPower
          ? t('chatWidgetHeaterDetailHeatingPower', { heatingPower: parsedHeatingPower })
          : null,
        parsedHeatingMediumLabel
          ? t('chatWidgetHeaterDetailHeatingMedium', {
              heatingMediumLabel: parsedHeatingMediumLabel
            })
          : null,
        parsedFanControlLabel
          ? t('chatWidgetHeaterDetailFanControl', { fanControlLabel: parsedFanControlLabel })
          : null,
        matchedProduct?.manufacturerProductCode
          ? t('chatWidgetHeaterDetailModelCode', {
              manufacturerProductCode: matchedProduct.manufacturerProductCode
            })
          : null,
        matchedProduct
          ? t('chatWidgetHeaterDetailCatalogProduct', {
              name: matchedProduct.name,
              id: matchedProduct.id
            })
          : null
      ].filter((part): part is string => Boolean(part && part.trim()))

      if (detailsParts.length === 0) {
        setHeaterLabelAiStatusMessage(t('chatWidgetAiNoHeaterDetailsFoundInImage'))
        return
      }

      const displaySummary = matchedProduct
        ? t('chatWidgetAiFoundProductSummary', {
            name: matchedProduct.name,
            powerLabel: matchedProduct.heatingPowerKw
              ? ` (${matchedProduct.heatingPowerKw} kW)`
              : ''
          })
        : t('chatWidgetAiNoCatalogProductSummary')
      const recognizedDetails = detailsParts.join(', ')

      setPendingInterpretedHeaterDetails({
        detailsMessage: detailsParts.join(', '),
        displaySummary
      })

      setHeaterLabelAiStatusMessage(null)
      pushAssistantMessage(
        t('chatWidgetAiRecognizedHeaterDetailsConfirm', {
          displaySummary,
          recognizedDetails,
          yes: quickYesLabel,
          no: quickNoLabel
        }),
        {
          decisionType: 'heater_details'
        }
      )
    } catch (_error) {
      if (isValveCustomerReferenceStep) {
        setHeaterLabelAiStatusMessage(null)
        pushAssistantMessage(t('chatWidgetAiNoCustomerReferenceFoundInImage'))
      } else {
        setHeaterLabelAiStatusMessage(t('chatWidgetAiHeaterInterpretationFailed'))
      }
    } finally {
      setIsReadingHeaterLabelWithAI(false)
    }
  }

  const handleDeviceImageUpload = async (file: File) => {
    const targetAssetId = deviceImageUploadAssetId

    if (!targetAssetId) {
      pushAssistantMessage(t('chatWidgetDeviceImageUploadMissingAsset'))
      return
    }

    setIsUploadingDeviceImage(true)

    try {
      const fileToUpload = await reduceImageFileSize(file, ASSET_PHOTO_UPLOAD_TARGET_BYTES)

      if (fileToUpload.size > ASSET_PHOTO_UPLOAD_MAX_BYTES) {
        pushAssistantMessage(t('chatWidgetDeviceImageUploadTooLarge'))
        return
      }

      await uploadAssetPhoto(targetAssetId, fileToUpload)

      setIsDeviceImageUploadDialogOpen(false)
      setDeviceImageUploadAssetId(null)

      if (isSpineOrValveSummaryConfirmationStep) {
        await processUserMessage(quickYesLabel, {
          selectedFromList: true,
          inputType: 'confirm'
        })
      }

      pushAssistantMessage(t('chatWidgetDeviceInstalledAndImageUploaded'))
    } catch (_error) {
      pushAssistantMessage(t('chatWidgetDeviceImageUploadFailed'))
    } finally {
      setIsUploadingDeviceImage(false)
    }
  }

  const handleInterpretedDeviceCodeDecision = async (shouldUseCode: boolean) => {
    if (isSending || !pendingInterpretedDeviceCode) {
      return
    }

    const pendingCode = pendingInterpretedDeviceCode
    setPendingInterpretedDeviceCode(null)
    setDeviceCodeAiStatusMessage(null)

    if (!shouldUseCode) {
      setDeviceCodeAiStatusMessage(t('chatWidgetDeviceCodeManualOrScanInstruction'))
      return
    }

    const valueToSend = pendingCode.matchedDeviceName ?? pendingCode.code

    if (!pendingCode.matchedDeviceName) {
      pushAssistantMessage(
        t('chatWidgetDeviceCodeNoMappingForwardedToBot', { code: pendingCode.code })
      )
    }

    await processUserMessage(valueToSend, {
      selectedFromList: Boolean(pendingCode.matchedDeviceName),
      inputType: 'device_select'
    })
  }

  const handleInterpretedHeaterDetailsDecision = async (shouldUseDetails: boolean) => {
    if (isSending || !pendingInterpretedHeaterDetails) {
      return
    }

    const pendingDetails = pendingInterpretedHeaterDetails
    setPendingInterpretedHeaterDetails(null)
    setHeaterLabelAiStatusMessage(null)

    if (!shouldUseDetails) {
      pushAssistantMessage(t('chatWidgetHeaterDetailsManualOrRetryInstruction'))
      focusChatInput()
      return
    }

    await processUserMessage(pendingDetails.detailsMessage, {
      selectedFromList: false,
      inputType: 'text'
    })
  }

  const handleInterpretedCustomerReferenceDecision = async (shouldUseReference: boolean) => {
    if (isSending || !pendingInterpretedCustomerReference) {
      return
    }

    const pendingReference = pendingInterpretedCustomerReference
    setPendingInterpretedCustomerReference(null)
    setHeaterLabelAiStatusMessage(null)

    if (!shouldUseReference) {
      pushAssistantMessage(t('chatWidgetCustomerReferenceManualOrRetryInstruction'))
      focusChatInput()
      return
    }

    const normalizedReference = normalizeCustomerReferenceValue(pendingReference.value)

    if (!normalizedReference) {
      pushAssistantMessage(t('chatWidgetCustomerReferenceInterpretationEmpty'))
      focusChatInput()
      return
    }

    await processUserMessage(normalizedReference, {
      selectedFromList: false,
      inputType: 'text'
    })
  }

  const handleOcrScannerResult = async (text: string) => {
    if (isSending || isHandlingQrScan || isReadingHeaterLabelWithAI) {
      return
    }

    setIsOcrScannerDialogOpen(false)
    setPendingInterpretedDeviceCode(null)

    const interpretedCode = extractCompleteShortCode(text)

    if (!interpretedCode) {
      setDeviceCodeAiStatusMessage(t('chatWidgetOcrNoReliableDeviceCode'))
      focusChatInput()
      return
    }

    const matchedDevice = resolveDeviceFromScannedCode(interpretedCode)
    const valueToSend = matchedDevice?.name ?? interpretedCode

    if (!matchedDevice) {
      pushAssistantMessage(
        t('chatWidgetOcrDeviceCodeNoMappingForwardedToBot', { code: interpretedCode })
      )
    }

    await processUserMessage(valueToSend, {
      selectedFromList: Boolean(matchedDevice),
      inputType: 'device_select'
    })
  }

  const submitAssistantOption = async (
    chosenValue: string,
    chosenInputType: AssistantSelectableInputType
  ) => {
    // Hide selector immediately for snappier UX; assistant will reopen if needed.
    setSelectedOption('')
    setAssistantInputType('text')
    setAssistantOptions([])

    if (chosenInputType === 'project_select') {
      const selectedProjectId = Number(chosenValue)

      if (Number.isFinite(selectedProjectId) && selectedProjectId > 0) {
        try {
          const devices = await getProjectDevices(selectedProjectId)
          const hydratedDeviceContext = mapProjectDevicesToChatContext(devices)
          setDeviceContext(hydratedDeviceContext)

          await processUserMessage(
            chosenValue,
            {
              selectedFromList: true,
              inputType: chosenInputType
            },
            {
              devices: hydratedDeviceContext
            }
          )

          return
        } catch (_error) {
          setDeviceContext([])
        }
      }
    }

    await processUserMessage(chosenValue, {
      selectedFromList: true,
      inputType: chosenInputType
    })
  }

  const handleOptionSubmit = async () => {
    if (!selectedOption) {
      return
    }

    const chosenValue = selectedOption
    const chosenInputType = assistantInputType as AssistantSelectableInputType

    await submitAssistantOption(chosenValue, chosenInputType)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      void sendMessage()
    }
  }

  const handleQuickReplyClick = async (prompt: string) => {
    const matchesPrompt = (...candidates: string[]): boolean => candidates.includes(prompt)

    if (isSending) {
      return
    }

    if (tryRespondToShowProjectsAgainRequest(prompt)) {
      return
    }

    if (pendingInterpretedDeviceCode) {
      const yesNoDecision = parseYesNoAnswer(prompt)

      if (yesNoDecision !== null) {
        await handleInterpretedDeviceCodeDecision(yesNoDecision)
        return
      }
    }

    if (pendingInterpretedHeaterDetails) {
      const yesNoDecision = parseYesNoAnswer(prompt)

      if (yesNoDecision !== null) {
        await handleInterpretedHeaterDetailsDecision(yesNoDecision)
        return
      }
    }

    if (pendingInterpretedCustomerReference) {
      const yesNoDecision = parseYesNoAnswer(prompt)

      if (yesNoDecision !== null) {
        await handleInterpretedCustomerReferenceDecision(yesNoDecision)
        return
      }
    }

    if (prompt === startOverPrompt) {
      resetConversation()
      return
    }

    if (prompt === goBackPrompt) {
      goBackOneAssistantStep()
      return
    }

    if (matchesPrompt(promptScanDeviceQrCode)) {
      setScanError(null)
      setScanDialogMode('device')
      setScanDialogOpen(true)
      return
    }

    if (matchesPrompt(promptScanTagQrCode)) {
      setTagSelectionMode('qr')
      setScanError(null)
      setScanDialogMode('tag')
      setScanDialogOpen(true)
      return
    }

    if (matchesPrompt(promptStartBluetoothTagScan) && assistantInputType === 'tag_select') {
      setTagSelectionMode('bluetooth')

      const deviceName = assistantState?.deviceName

      if (!deviceName) {
        pushAssistantMessage(t('chatWidgetBluetoothScanNoDeviceName'))
        return
      }

      void startRuuviTagBluetoothScan(deviceName, {
        announceStartMessage: false,
        notifyProgressMessages: true,
        resetDiscoveredTags: false
      }).catch((_error) => {
        stopTagScan()
        pushAssistantMessage(t('chatWidgetBluetoothScanStartFailedEnterTagManually'))
      })

      return
    }

    if (matchesPrompt(promptUploadDeviceImage) && isSpineOrValveSummaryConfirmationStep) {
      await openDeviceImageUploadDialog()
      return
    }

    if (matchesPrompt(promptTryAiInterpretationFromImage)) {
      if (isHostDeviceSelectionWithoutAi) {
        pushAssistantMessage(t('chatWidgetThisStepUseQrOrManualDeviceCode'))
        focusChatInput()
        return
      }

      openOcrScannerDialog()
      return
    }

    if (matchesPrompt(promptReadFromImage) && isValveCustomerReferenceEntryMode) {
      heaterLabelImageInputRef.current?.click()
      return
    }

    if (matchesPrompt(promptEnterManually) && isValveCustomerReferenceEntryMode) {
      pushAssistantMessage(t('chatWidgetEnterHeaterCustomerReferenceInChat'))
      focusChatInput()
      return
    }

    if (matchesPrompt(promptReadNameplateFromImage)) {
      heaterLabelImageInputRef.current?.click()
      return
    }

    if (matchesPrompt(promptEnterNameManually)) {
      pushAssistantMessage(t('chatWidgetEnterCorrectHeaterNameInChat'))
      focusChatInput()
      return
    }

    if (matchesPrompt(promptEnterManufacturerManually)) {
      pushAssistantMessage(t('chatWidgetEnterHeaterManufacturerInChat'))
      focusChatInput()
      return
    }

    if (matchesPrompt(promptEnterProductCodeManually)) {
      pushAssistantMessage(t('chatWidgetEnterHeaterProductCodeInChat'))
      focusChatInput()
      return
    }

    if (matchesPrompt(promptEnterPowerManually)) {
      pushAssistantMessage(t('chatWidgetEnterHeaterPowerInChat'))
      focusChatInput()
      return
    }

    if (matchesPrompt(promptEnterDetailsManually, promptEnterDeviceDetails)) {
      pushAssistantMessage(t('chatWidgetEnterDeviceDetailsInChat'))
      focusChatInput()
      return
    }

    if (matchesPrompt(promptEnter6DigitDeviceCode)) {
      setDeviceCodeAiStatusMessage(enter6DigitCodePromptText)
      focusChatInput()
      return
    }

    if (assistantInputType === 'confirm') {
      await processUserMessage(prompt, {
        selectedFromList: true,
        inputType: 'confirm'
      })
      return
    }

    const shouldMarkQuickReplyAsSelection = isPageVariant || assistantInputType !== 'text'

    if (shouldMarkQuickReplyAsSelection) {
      await processUserMessage(prompt, {
        selectedFromList: true,
        inputType: assistantInputType
      })
      return
    }

    await processUserMessage(prompt)
  }

  useEffect(() => {
    if (!isPageVariant) {
      return
    }

    setOpen(true)
    setShowDeviceCodeExampleImage(true)
  }, [isPageVariant])

  useEffect(() => {
    const handleOpenAiAgentChat = () => {
      setOpen(true)
    }

    window.addEventListener('spine:open-ai-agent-chat', handleOpenAiAgentChat)

    return () => {
      window.removeEventListener('spine:open-ai-agent-chat', handleOpenAiAgentChat)
    }
  }, [])

  return (
    <Box
      sx={
        isPageVariant
          ? {
              width: '100%',
              height: '100%',
              minHeight: 0,
              py: isMobile ? 1 : 0,
              display: 'flex',
              flexDirection: 'column'
            }
          : {
              position: 'fixed',
              right: isMobile ? 16 : 24,
              bottom: isMobile ? 88 : 72,
              zIndex: 1300
            }
      }
    >
      {!isPageVariant && !open ? (
        <Fab color="primary" aria-label={openChatAriaLabel} onClick={() => setOpen(true)}>
          <ChatIcon />
        </Fab>
      ) : (
        <Paper
          elevation={8}
          sx={{
            width: isPageVariant ? '100%' : isMobile ? 'calc(100vw - 24px)' : 500,
            height: isPageVariant
              ? isMobile
                ? 'calc(100% - env(safe-area-inset-bottom) - 8px)'
                : '100%'
              : isMobile
                ? '78vh'
                : 680,
            maxHeight: isPageVariant
              ? isMobile
                ? 'calc(100% - env(safe-area-inset-bottom) - 8px)'
                : '100%'
              : isMobile
                ? 'calc(100vh - 110px)'
                : 'calc(100vh - 120px)',
            minHeight: 0,
            flexGrow: isPageVariant ? 1 : 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: isPageVariant ? 2 : 3
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.5 }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {chatPanelTitle}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {chatPanelSubtitle}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5}>
              <IconButton aria-label={newConversationAriaLabel} onClick={resetConversation}>
                <RestartAlt />
              </IconButton>
              {!isPageVariant && (
                <IconButton aria-label={closeChatAriaLabel} onClick={() => setOpen(false)}>
                  <Close />
                </IconButton>
              )}
            </Stack>
          </Stack>
          <Divider />

          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              overscrollBehaviorY: 'contain',
              p: 2,
              bgcolor: theme.palette.background.default
            }}
          >
            <Stack spacing={1.5}>
              {messages.map((message, index) => {
                const showInlineCustomerReferenceDecision =
                  message.decisionType === 'customer_reference' &&
                  pendingInterpretedCustomerReference !== null &&
                  index === messages.length - 1
                const showInlineHeaterDetailsDecision =
                  message.decisionType === 'heater_details' &&
                  pendingInterpretedHeaterDetails !== null &&
                  index === messages.length - 1

                if (message.kind === 'valve_customer_reference_example') {
                  return (
                    <Box
                      key={message.id}
                      sx={{
                        alignSelf: 'flex-start',
                        maxWidth: '90%',
                        px: 1.25,
                        py: 1,
                        borderRadius: 2,
                        bgcolor: theme.palette.background.paper,
                        boxShadow: 1
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 0.75 }}
                      >
                        {t('chatWidgetCustomerReferenceExampleTitle')}
                      </Typography>
                      <Box
                        component="img"
                        src="/static/laitekoodi.jpg"
                        alt={String(t('chatWidgetCustomerReferenceExampleAlt'))}
                        onLoad={scrollToBottom}
                        sx={{
                          width: '100%',
                          maxWidth: isPageVariant ? 360 : 210,
                          maxHeight: isPageVariant ? 260 : 170,
                          objectFit: 'contain',
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          display: 'block'
                        }}
                      />

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.75 }}
                      >
                        {t('chatWidgetCustomerReferenceExampleTip')}
                      </Typography>
                    </Box>
                  )
                }

                if (message.kind === 'valve_heater_nameplate_example') {
                  return (
                    <Box
                      key={message.id}
                      sx={{
                        alignSelf: 'flex-start',
                        maxWidth: '90%',
                        px: 1.25,
                        py: 1,
                        borderRadius: 2,
                        bgcolor: theme.palette.background.paper,
                        boxShadow: 1
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 1 }}
                      >
                        {t('chatWidgetHeaterDetailsManualOrImageHint')}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 0.75 }}
                      >
                        {t('chatWidgetHeaterNameplateExampleTitle')}
                      </Typography>
                      <Box
                        component="img"
                        src="/static/1000026070.jpg"
                        alt={String(t('chatWidgetHeaterNameplateExampleAlt'))}
                        onLoad={scrollToBottom}
                        sx={{
                          width: '100%',
                          maxWidth: isPageVariant ? 360 : 210,
                          maxHeight: isPageVariant ? 260 : 170,
                          objectFit: 'contain',
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          display: 'block'
                        }}
                      />

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.75 }}
                      >
                        {t('chatWidgetHeaterNameplateExampleTip')}
                      </Typography>
                    </Box>
                  )
                }

                return (
                  <Box
                    key={message.id}
                    sx={{
                      display: 'flex',
                      justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start'
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: '80%',
                        px: 1.5,
                        py: 1,
                        borderRadius: 2,
                        bgcolor:
                          message.role === 'user'
                            ? theme.palette.primary.main
                            : theme.palette.background.paper,
                        color:
                          message.role === 'user'
                            ? theme.palette.primary.contrastText
                            : theme.palette.text.primary,
                        boxShadow: 1
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                      >
                        {message.content}
                      </Typography>

                      {showInlineCustomerReferenceDecision && (
                        <Stack
                          direction="row"
                          spacing={1}
                          useFlexGap
                          flexWrap="wrap"
                          sx={{ mt: 1 }}
                        >
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => {
                              void handleInterpretedCustomerReferenceDecision(true)
                            }}
                            disabled={
                              isSending ||
                              isHandlingQrScan ||
                              isReadingDeviceCodeWithAI ||
                              isReadingHeaterLabelWithAI
                            }
                          >
                            {quickYesLabel}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              void handleInterpretedCustomerReferenceDecision(false)
                            }}
                            disabled={
                              isSending ||
                              isHandlingQrScan ||
                              isReadingDeviceCodeWithAI ||
                              isReadingHeaterLabelWithAI
                            }
                          >
                            {quickNoLabel}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={goBackOneAssistantStep}
                            disabled={
                              isSending ||
                              isHandlingQrScan ||
                              isReadingDeviceCodeWithAI ||
                              isReadingHeaterLabelWithAI
                            }
                          >
                            {goBackPrompt}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={resetConversation}
                            disabled={
                              isSending ||
                              isHandlingQrScan ||
                              isReadingDeviceCodeWithAI ||
                              isReadingHeaterLabelWithAI
                            }
                          >
                            {startOverPrompt}
                          </Button>
                        </Stack>
                      )}

                      {showInlineHeaterDetailsDecision && (
                        <Stack
                          direction="row"
                          spacing={1}
                          useFlexGap
                          flexWrap="wrap"
                          sx={{ mt: 1 }}
                        >
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => {
                              void handleInterpretedHeaterDetailsDecision(true)
                            }}
                            disabled={
                              isSending ||
                              isHandlingQrScan ||
                              isReadingDeviceCodeWithAI ||
                              isReadingHeaterLabelWithAI
                            }
                          >
                            {quickYesLabel}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              void handleInterpretedHeaterDetailsDecision(false)
                            }}
                            disabled={
                              isSending ||
                              isHandlingQrScan ||
                              isReadingDeviceCodeWithAI ||
                              isReadingHeaterLabelWithAI
                            }
                          >
                            {quickNoLabel}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={goBackOneAssistantStep}
                            disabled={
                              isSending ||
                              isHandlingQrScan ||
                              isReadingDeviceCodeWithAI ||
                              isReadingHeaterLabelWithAI
                            }
                          >
                            {goBackPrompt}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={resetConversation}
                            disabled={
                              isSending ||
                              isHandlingQrScan ||
                              isReadingDeviceCodeWithAI ||
                              isReadingHeaterLabelWithAI
                            }
                          >
                            {startOverPrompt}
                          </Button>
                        </Stack>
                      )}
                    </Box>
                  </Box>
                )
              })}

              {shouldShowDeviceSelectionExampleImage && (
                <Box
                  sx={{
                    alignSelf: 'flex-start',
                    maxWidth: '90%',
                    px: 1.25,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: theme.palette.background.paper,
                    boxShadow: 1
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.75 }}
                  >
                    {t('chatWidgetDeviceCodeLocationExampleTitle')}
                  </Typography>
                  <Box
                    component="img"
                    src="/static/laitekoodi.jpg"
                    alt={String(t('chatWidgetDeviceCodeLocationExampleAlt'))}
                    onLoad={scrollToBottom}
                    sx={{
                      width: '100%',
                      maxWidth: isPageVariant ? 260 : 150,
                      maxHeight: isPageVariant ? 260 : 110,
                      objectFit: 'contain',
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      display: 'block'
                    }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.75 }}
                  >
                    {deviceCodeUnderQrHintText}
                  </Typography>
                </Box>
              )}

              {!isSending &&
                (assistantInputType === 'project_select' ||
                  assistantInputType === 'device_select') &&
                !isDeviceCodeEntryMode &&
                !isExistingInstallationStrategySelection &&
                !isValveTagStrategySelection && (
                  <Box
                    sx={{
                      alignSelf: 'flex-start',
                      maxWidth: '90%',
                      px: 1.25,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: theme.palette.background.paper,
                      boxShadow: 1
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 1 }}
                    >
                      {assistantInputType === 'project_select'
                        ? projectSelectGuidanceText
                        : deviceSelectHintText}
                    </Typography>
                    {assistantInputType === 'project_select' && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <FormControl size="small" fullWidth>
                          <InputLabel id="chat-select-label">{projectSelectLabel}</InputLabel>
                          <Select
                            labelId="chat-select-label"
                            value={selectedOption}
                            label={projectSelectLabel}
                            onChange={(event) => setSelectedOption(event.target.value)}
                            disabled={isSending}
                          >
                            {rankedProjectOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <Button
                          variant="contained"
                          onClick={() => void handleOptionSubmit()}
                          disabled={
                            !selectedOption ||
                            isSending ||
                            isReadingDeviceCodeWithAI ||
                            rankedProjectOptions.length === 0
                          }
                        >
                          {selectActionLabel}
                        </Button>
                      </Stack>
                    )}

                    {assistantInputType === 'project_select' && projectQuickOptions.length > 0 && (
                      <Box sx={{ mt: 1.25 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mb: 0.75 }}
                        >
                          {projectQuickOptionsTitle}
                        </Typography>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                          {projectQuickOptions.map((projectOption) => (
                            <Button
                              key={projectOption.value}
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                void submitAssistantOption(projectOption.value, 'project_select')
                              }}
                              disabled={isSending || isHandlingQrScan || isReadingDeviceCodeWithAI}
                              sx={{ textTransform: 'none' }}
                            >
                              {projectOption.label}
                            </Button>
                          ))}
                        </Stack>
                      </Box>
                    )}

                    {assistantInputType === 'device_select' && (
                      <Box sx={{ mt: 1.25 }}>
                        {!isPageVariant && !isSpecialDeviceSelectStep && (
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => setShowDeviceCodeExampleImage((previous) => !previous)}
                            sx={{ mt: 0.5, px: 0, textTransform: 'none' }}
                          >
                            {showDeviceCodeExampleImage
                              ? hideExampleImageLabel
                              : showExampleImageLabel}
                          </Button>
                        )}

                        {canUseDeviceCodeToolsInDeviceSelect &&
                          canUseDeviceCodeAiInterpretation &&
                          deviceCodeAiStatusMessage && (
                            <Box
                              sx={{
                                mt: 1,
                                px: 1,
                                py: 0.75,
                                borderRadius: 1,
                                border: `1px solid ${theme.palette.divider}`,
                                bgcolor: theme.palette.background.default
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', whiteSpace: 'pre-wrap' }}
                              >
                                {deviceCodeAiStatusMessage}
                              </Typography>
                            </Box>
                          )}

                        {canUseDeviceCodeToolsInDeviceSelect &&
                          canUseDeviceCodeAiInterpretation &&
                          pendingInterpretedDeviceCode && (
                            <Stack
                              direction="row"
                              spacing={1}
                              useFlexGap
                              flexWrap="wrap"
                              sx={{ mt: 1 }}
                            >
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => {
                                  void handleInterpretedDeviceCodeDecision(true)
                                }}
                                disabled={
                                  isSending || isHandlingQrScan || isReadingDeviceCodeWithAI
                                }
                              >
                                {quickYesLabel}
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  void handleInterpretedDeviceCodeDecision(false)
                                }}
                                disabled={
                                  isSending || isHandlingQrScan || isReadingDeviceCodeWithAI
                                }
                              >
                                {quickNoLabel}
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={goBackOneAssistantStep}
                                disabled={
                                  isSending || isHandlingQrScan || isReadingDeviceCodeWithAI
                                }
                              >
                                {goBackPrompt}
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={resetConversation}
                                disabled={
                                  isSending || isHandlingQrScan || isReadingDeviceCodeWithAI
                                }
                              >
                                {startOverPrompt}
                              </Button>
                            </Stack>
                          )}

                        {!isExistingInstallationStrategySelection && (
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                            <FormControl size="small" fullWidth>
                              <InputLabel id="chat-device-select-label">
                                {deviceSelectInputLabel}
                              </InputLabel>
                              <Select
                                labelId="chat-device-select-label"
                                value={selectedOption}
                                label={deviceSelectInputLabel}
                                onChange={(event) => setSelectedOption(event.target.value)}
                                disabled={isSending}
                              >
                                {effectiveOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>

                            <Button
                              variant="contained"
                              onClick={() => void handleOptionSubmit()}
                              disabled={
                                !selectedOption ||
                                isSending ||
                                isReadingDeviceCodeWithAI ||
                                effectiveOptions.length === 0
                              }
                            >
                              {selectActionLabel}
                            </Button>
                          </Stack>
                        )}

                        {canUseDeviceCodeToolsInDeviceSelect && (
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ mt: 1 }}
                          >
                            <Button
                              variant="outlined"
                              startIcon={<QrCodeScanner />}
                              onClick={() => {
                                setScanError(null)
                                setScanDialogMode('device')
                                setScanDialogOpen(true)
                              }}
                              disabled={isSending || isHandlingQrScan || isReadingDeviceCodeWithAI}
                            >
                              {promptScanDeviceQrCode}
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={() => {
                                pushAssistantMessage(manualDeviceEntryInstruction)
                                focusChatInput()
                              }}
                              disabled={isSending || isHandlingQrScan || isReadingDeviceCodeWithAI}
                            >
                              {promptEnterManually}
                            </Button>
                            {canUseDeviceCodeAiInterpretation && (
                              <Button
                                variant="outlined"
                                onClick={openOcrScannerDialog}
                                disabled={
                                  isSending || isHandlingQrScan || isReadingDeviceCodeWithAI
                                }
                              >
                                {promptTryAiInterpretationFromImage}
                              </Button>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {deviceCodeUnderQrHintText}
                            </Typography>
                          </Stack>
                        )}

                        <input
                          ref={deviceCodeImageInputRef}
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={(event) => {
                            const file = event.target.files?.[0]

                            if (file) {
                              void handleDeviceCodeImageUpload(file)
                            }

                            event.target.value = ''
                          }}
                        />
                      </Box>
                    )}

                    {!isExistingInstallationStrategySelection && effectiveOptions.length === 0 && (
                      <Typography
                        variant="caption"
                        color="warning.main"
                        sx={{ display: 'block', mt: 1 }}
                      >
                        {noOptionsAvailableHintText}
                      </Typography>
                    )}
                  </Box>
                )}

              {!isSending && isDeviceCodeEntryMode && (
                <Box
                  sx={{
                    alignSelf: 'flex-start',
                    maxWidth: '90%',
                    px: 1.25,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: theme.palette.background.paper,
                    boxShadow: 1
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 1 }}
                  >
                    {deviceCodeEntryModeHintText}
                  </Typography>

                  {deviceCodeAiStatusMessage && (
                    <Box
                      sx={{
                        mt: 1,
                        px: 1,
                        py: 0.75,
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        bgcolor: theme.palette.background.default
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', whiteSpace: 'pre-wrap' }}
                      >
                        {deviceCodeAiStatusMessage}
                      </Typography>
                    </Box>
                  )}

                  {pendingInterpretedDeviceCode && (
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          void handleInterpretedDeviceCodeDecision(true)
                        }}
                        disabled={isSending || isHandlingQrScan || isReadingDeviceCodeWithAI}
                      >
                        {quickYesLabel}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          void handleInterpretedDeviceCodeDecision(false)
                        }}
                        disabled={isSending || isHandlingQrScan || isReadingDeviceCodeWithAI}
                      >
                        {quickNoLabel}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={goBackOneAssistantStep}
                        disabled={isSending || isHandlingQrScan || isReadingDeviceCodeWithAI}
                      >
                        {goBackPrompt}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={resetConversation}
                        disabled={isSending || isHandlingQrScan || isReadingDeviceCodeWithAI}
                      >
                        {startOverPrompt}
                      </Button>
                    </Stack>
                  )}

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mt: 1 }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<QrCodeScanner />}
                      onClick={() => {
                        setScanError(null)
                        setScanDialogMode('device')
                        setScanDialogOpen(true)
                      }}
                      disabled={isSending || isHandlingQrScan || isReadingDeviceCodeWithAI}
                    >
                      {promptScanDeviceQrCode}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setDeviceCodeAiStatusMessage(enter6DigitCodePromptText)
                        focusChatInput()
                      }}
                      disabled={isSending || isHandlingQrScan || isReadingDeviceCodeWithAI}
                    >
                      {promptEnter6DigitDeviceCode}
                    </Button>
                  </Stack>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.75 }}
                  >
                    {deviceCodeUnderQrHintText}
                  </Typography>

                  <input
                    ref={deviceCodeImageInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(event) => {
                      const file = event.target.files?.[0]

                      if (file) {
                        void handleDeviceCodeImageUpload(file)
                      }

                      event.target.value = ''
                    }}
                  />
                </Box>
              )}

              {shouldShowTagQrExampleImage && (
                <Box
                  sx={{
                    alignSelf: 'flex-start',
                    maxWidth: '90%',
                    px: 1.25,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: theme.palette.background.paper,
                    boxShadow: 1
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.75 }}
                  >
                    {t('chatWidgetTagQrExampleTitle')}
                  </Typography>
                  <Box
                    component="img"
                    src="/static/kuva.png"
                    alt={String(t('chatWidgetTagQrExampleAlt'))}
                    onLoad={scrollToBottom}
                    sx={{
                      mt: 1,
                      width: '100%',
                      maxWidth: 220,
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      display: 'block'
                    }}
                  />
                </Box>
              )}

              {!isSending && assistantInputType === 'tag_select' && (
                <Box
                  sx={{
                    alignSelf: 'flex-start',
                    width: '100%',
                    px: 1.25,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: theme.palette.background.paper,
                    boxShadow: 1
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 1 }}
                  >
                    {t('chatWidgetChooseTagInputMethod')}
                  </Typography>

                  {tagSelectionMode === 'qr' ? (
                    <Box sx={{ mt: 1.25 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 0.75 }}
                      >
                        {t('chatWidgetScanTagQrOrEnterManually')}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ mt: 1.25 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 0.75 }}
                      >
                        {availableDiscoveredTags.length > 0
                          ? t('chatWidgetSelectFoundTagDuringScan')
                          : t('chatWidgetTagResultsAppearAutomatically')}
                      </Typography>

                      {availableDiscoveredTags.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          {t('chatWidgetWaitOrEnterTagManually')}
                        </Typography>
                      ) : (
                        <Stack spacing={1}>
                          {availableDiscoveredTags.map((tag) => {
                            const acceleration = tag.latest?.acceleration
                            const rssi = tag.latest?.condition?.rssi ?? acceleration?.rssi
                            const x = acceleration?.xAvg ?? acceleration?.xMax ?? '—'
                            const y = acceleration?.yAvg ?? acceleration?.yMax ?? '—'
                            const z = acceleration?.zAvg ?? acceleration?.zMax ?? '—'

                            return (
                              <Box
                                key={tag.id}
                                sx={{
                                  border: `1px solid ${theme.palette.divider}`,
                                  borderRadius: 1,
                                  px: 1,
                                  py: 1,
                                  display: 'grid',
                                  gridTemplateColumns: '1fr auto',
                                  gap: 1,
                                  alignItems: 'center'
                                }}
                              >
                                <Box>
                                  <Typography variant="body2" fontWeight={600}>
                                    {formatMacAddress(tag.id)}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block' }}
                                  >
                                    {rssiLabel}: {typeof rssi === 'number' ? rssi : '—'}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block' }}
                                  >
                                    {orientationLabel}: {String(x)} / {String(y)} / {String(z)}
                                  </Typography>
                                </Box>

                                <Box
                                  sx={{
                                    width: 76,
                                    height: 76,
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    border: `1px solid ${theme.palette.divider}`,
                                    cursor: 'pointer'
                                  }}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => setPreviewTagId(tag.id)}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault()
                                      setPreviewTagId(tag.id)
                                    }
                                  }}
                                >
                                  <Snapshot3D
                                    modelUrl="/static/ruuvitag-pro-simplified-enclosure.glb"
                                    rotationDeg={computeRotationDegFromTag(tag)}
                                    background={
                                      theme.palette.mode === 'light' ? 0xf7f9fc : 0x1b2635
                                    }
                                    style={{ width: '100%', height: '100%' }}
                                  />
                                </Box>

                                <Box
                                  sx={{
                                    gridColumn: '1 / -1',
                                    display: 'flex',
                                    justifyContent: 'flex-end'
                                  }}
                                >
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => {
                                      void handleTagCardSelect(tag.id)
                                    }}
                                    disabled={isSending}
                                  >
                                    {selectTagActionLabel}
                                  </Button>
                                </Box>
                              </Box>
                            )
                          })}
                        </Stack>
                      )}
                    </Box>
                  )}

                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
                    <Button
                      variant={tagSelectionMode === 'qr' ? 'contained' : 'outlined'}
                      size="small"
                      startIcon={<QrCodeScanner />}
                      onClick={() => {
                        setTagSelectionMode('qr')
                        setScanError(null)
                        setScanDialogMode('tag')
                        setScanDialogOpen(true)
                      }}
                      disabled={isSending || isHandlingQrScan || isReadingDeviceCodeWithAI}
                    >
                      {scanQrCodeActionLabel}
                    </Button>
                    <Button
                      variant={tagSelectionMode === 'bluetooth' ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => {
                        setTagSelectionMode('bluetooth')

                        const deviceName = assistantState?.deviceName

                        if (!deviceName) {
                          return
                        }

                        void startRuuviTagBluetoothScan(deviceName, {
                          announceStartMessage: false,
                          notifyProgressMessages: false,
                          resetDiscoveredTags: false
                        }).catch(() => {
                          // Keep QR/manual flow available even if scan start fails here.
                        })
                      }}
                      disabled={isHandlingQrScan || isReadingDeviceCodeWithAI}
                    >
                      {bluetoothListActionLabel}
                    </Button>
                  </Stack>

                  {activeQuickPrompts.length > 0 && (
                    <>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 1.25, mb: 1 }}
                      >
                        {quickPromptSectionTitle}
                      </Typography>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {activeQuickPrompts.map((prompt) => (
                          <Button
                            key={prompt}
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              void handleQuickReplyClick(prompt)
                            }}
                            disabled={isSending}
                            sx={{ textTransform: 'none' }}
                          >
                            {prompt}
                          </Button>
                        ))}
                      </Stack>
                    </>
                  )}
                </Box>
              )}

              {!isSending && shouldShowHeaterLabelStatusMessage && heaterLabelAiStatusMessage && (
                <Box
                  sx={{
                    alignSelf: 'flex-start',
                    maxWidth: '80%',
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    boxShadow: 1
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                  >
                    {heaterLabelAiStatusMessage}
                  </Typography>
                </Box>
              )}

              {!isSending &&
                activeQuickPrompts.length > 0 &&
                !isDeviceCodeEntryMode &&
                !pendingInterpretedCustomerReference &&
                !pendingInterpretedHeaterDetails &&
                assistantInputType !== 'tag_select' && (
                  <Box
                    sx={{
                      alignSelf: 'flex-start',
                      maxWidth: '95%',
                      px: 1.25,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: theme.palette.background.paper,
                      boxShadow: 1
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 1 }}
                    >
                      {isAiInterpretationQuickReplyMode
                        ? aiInterpretationSectionTitle
                        : quickPromptSectionTitle}
                    </Typography>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {activeQuickPrompts.map((prompt) => (
                        <Button
                          key={prompt}
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            void handleQuickReplyClick(prompt)
                          }}
                          disabled={isSending}
                          sx={{ textTransform: 'none' }}
                        >
                          {prompt}
                        </Button>
                      ))}
                    </Stack>
                  </Box>
                )}

              {isSending && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <Box
                    sx={{
                      maxWidth: '80%',
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: theme.palette.background.paper,
                      color: theme.palette.text.secondary,
                      boxShadow: 1
                    }}
                  >
                    <Typography variant="body2">{assistantTypingLabel}</Typography>
                  </Box>
                </Box>
              )}
              <div ref={endRef} />
            </Stack>
          </Box>

          <Divider />

          <Stack
            direction="row"
            spacing={1}
            sx={{
              p: 1.5,
              pb: isPageVariant && isMobile ? 'calc(env(safe-area-inset-bottom) + 12px)' : 1.5
            }}
            alignItems="flex-end"
          >
            <TextField
              inputRef={chatInputRef}
              fullWidth
              size="small"
              placeholder={messagePlaceholder}
              multiline
              maxRows={4}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSending}
            />
            <IconButton
              color="primary"
              aria-label={sendMessageAriaLabel}
              onClick={() => void sendMessage()}
              disabled={!input.trim() || isSending}
            >
              <Send />
            </IconButton>
          </Stack>

          <input
            ref={heaterLabelImageInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(event) => {
              const file = event.target.files?.[0]

              if (file) {
                void handleHeaterLabelImageUpload(file)
              }

              event.target.value = ''
            }}
          />

          <Dialog
            open={Boolean(previewTag)}
            onClose={() => setPreviewTagId(null)}
            fullScreen={isMobile}
            fullWidth
            maxWidth="md"
          >
            <DialogTitle>
              {previewTag
                ? t('chatWidgetTagPreviewDialogTitle', { id: formatMacAddress(previewTag.id) })
                : t('chatWidgetTagPreviewDialogFallbackTitle')}
            </DialogTitle>
            <DialogContent>
              {previewTag && (
                <>
                  <Box
                    sx={{
                      width: '100%',
                      height: isMobile ? 300 : 460,
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: `1px solid ${theme.palette.divider}`,
                      mb: 1.5
                    }}
                  >
                    <Snapshot3D
                      modelUrl="/static/ruuvitag-pro-simplified-enclosure.glb"
                      rotationDeg={computeRotationDegFromTag(previewTag)}
                      interactive={true}
                      background={theme.palette.mode === 'light' ? 0xf7f9fc : 0x1b2635}
                      style={{ width: '100%', height: '100%' }}
                    />
                  </Box>

                  <Typography variant="body2" color="text.secondary">
                    {rssiLabel}:{' '}
                    {typeof (
                      previewTag.latest?.condition?.rssi ?? previewTag.latest?.acceleration?.rssi
                    ) === 'number'
                      ? (previewTag.latest?.condition?.rssi ??
                        previewTag.latest?.acceleration?.rssi)
                      : '—'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {orientationLabel}:{' '}
                    {String(
                      previewTag.latest?.acceleration?.xAvg ??
                        previewTag.latest?.acceleration?.xMax ??
                        '—'
                    )}{' '}
                    /{' '}
                    {String(
                      previewTag.latest?.acceleration?.yAvg ??
                        previewTag.latest?.acceleration?.yMax ??
                        '—'
                    )}{' '}
                    /{' '}
                    {String(
                      previewTag.latest?.acceleration?.zAvg ??
                        previewTag.latest?.acceleration?.zMax ??
                        '—'
                    )}
                  </Typography>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPreviewTagId(null)}>{closeActionLabel}</Button>
              <Button
                variant="contained"
                onClick={() => {
                  if (!previewTag) {
                    return
                  }

                  setPreviewTagId(null)
                  void handleTagCardSelect(previewTag.id)
                }}
                disabled={!previewTag || isSending}
              >
                {selectThisTagActionLabel}
              </Button>
            </DialogActions>
          </Dialog>

          <ChatWidgetQrScanDialog
            open={scanDialogOpen}
            mode={scanDialogMode}
            scanError={scanError}
            onClose={() => setScanDialogOpen(false)}
            onQrResult={handleQrScanResult}
          />

          <ChatWidgetOcrDialog
            open={isOcrScannerDialogOpen}
            isMobile={isMobile}
            isSending={isSending}
            isHandlingQrScan={isHandlingQrScan}
            isReadingDeviceCodeWithAI={isReadingDeviceCodeWithAI}
            isBusy={isOcrScannerBusy}
            onBusyChange={setIsOcrScannerBusy}
            onClose={closeOcrScannerDialog}
            onSelectImageFile={submitOcrImageFile}
            onOcrTextResult={handleOcrScannerResult}
          />

          <ChatWidgetDeviceImageUploadDialog
            open={isDeviceImageUploadDialogOpen}
            isUploading={isUploadingDeviceImage}
            onClose={closeDeviceImageUploadDialog}
            onUpload={handleDeviceImageUpload}
          />
        </Paper>
      )}
    </Box>
  )
}
